import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  IconButton,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Tooltip,
  Alert,
  CircularProgress,
  TablePagination,
  SelectChangeEvent,
  Tabs,
  Tab
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import CalculateIcon from '@mui/icons-material/Calculate';
import SaveIcon from '@mui/icons-material/Save';
import PrintIcon from '@mui/icons-material/Print';
import ReceiptIcon from '@mui/icons-material/Receipt';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import NotListedLocationIcon from '@mui/icons-material/NotListedLocation';
import DraftsIcon from '@mui/icons-material/Drafts';
import WarningIcon from '@mui/icons-material/Warning';
import HistoryIcon from '@mui/icons-material/History';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import ownAuthSession from '../../../services/auth/ownAuthSession';
import firebaseDataService from '../../../services/FirebaseDataService';
import { useAuth } from '../../../contexts/AuthContext';
import { UNIT_OPTIONS } from '../../../constants/units';
import { getSellerByCompany, type SellerSnapshot } from '../../../services/sellerInfoService';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import MenuIcon from '@mui/icons-material/Menu';
import Menu from '@mui/material/Menu';
import { ACCOUNTING_API, EINVOICE_API, handleApiError } from '../../../config/api';
import { accountingApi, einvoiceApi } from '../../../config/apiClient';
import { setFormDirty } from '../../../utils/formLeaveGuard';
import { formatAmount } from '../../../utils/numberFormat';
import { AppDatePicker } from '../../common/AppDatePicker';
import GuideButton from '../../common/guide/GuideButton';
import { useInventoryData } from '../inventory/hooks/useInventoryData';
import type { InventoryItem, WarehouseStockDto } from '../inventory/types/inventory.types';
import { WarehouseService } from '../inventory/services';
import PdfInvoiceUploadModal from './PdfInvoiceUploadModal';

// ✅ Hook'lar ve component'ler
import { useInvoiceCalculations } from './hooks/useInvoiceCalculations';
import { useInvoiceAgentBridge } from './hooks/useInvoiceAgentBridge';
import { useAccountSelection } from './hooks/useAccountSelection';
import { InvoiceItemsTable } from './components/tables/InvoiceItemsTable';
import { AccountSelectionModal } from './components/modals/AccountSelectionModal';
import { InvoiceSuccessModal } from './components/modals/InvoiceSuccessModal';
import { CopyItemsFromDocumentModal } from './components/modals/CopyItemsFromDocumentModal';
import { DraftInvoiceModal } from './components/modals/DraftInvoiceModal';
import { BulkWarehouseModal } from './components/modals/BulkWarehouseModal';
import { StockProcessModal } from './components/modals/StockProcessModal';
import { AccountInfoForm } from './components/forms/AccountInfoForm';
import { InvoiceInfoForm } from './components/forms/InvoiceInfoForm';
import ProjectPickerButton from '../shared/ProjectPickerButton';
import { EInvoiceDetailsForm } from './components/forms/EInvoiceDetailsForm';
import { NotesAndSummarySection } from './components/sections/NotesAndSummarySection';
import { parseQuantityString, formatQuantityForDisplay, recalculateLineItem, formatCurrency, getCurrencySymbol, tevkifatTypes } from './utils/invoiceCalculations';
import { setItemDisplay, commitItemNumber, clearDiscountDisplays, findEmptyRequiredFields, type EditableNumberField } from './utils/invoiceItemFields';
import { invoiceLotPayload, lotFlagsFromItem, readInvoiceLot } from './utils/invoiceLot';
import { addScannedLine } from './utils/scannedLine';
import type { ScannedInvoiceItem } from './components/BarcodeScanField';
import { ORDER_STOCK_HANDOVER_MESSAGE, isOrderStockProcessed } from './utils/orderStockHandover';
import { 
  EInvoiceProfileType, 
  EInvoiceTypeCode, 
  EInvoiceScenario,
  ProfileTypeToScenarioMap
} from './components/types/einvoice.types';

// ✅ Type'lar
import type { Account, InvoiceItem, Invoice, Warehouse, TevkifatType } from './components/types/invoice.types';

// ✅ Styled component'ler
import { InvoiceHeader, InvoiceSection, SectionTitle, TotalSection, TotalLine } from './components/styled/InvoiceStyledComponents';

// ✅ Props interface - Teklif, İrsaliye ve Sipariş modu desteği
interface SalesInvoiceProps {
  mode?: 'invoice' | 'offer' | 'waybill' | 'order'; // Varsayılan: 'invoice'
}

const SalesInvoice: React.FC<SalesInvoiceProps> = ({ mode = 'invoice' }) => {
  const isOfferMode = mode === 'offer';
  const isWaybillMode = mode === 'waybill';
  const isOrderMode = mode === 'order';
  const { warehouses, companyName } = useAuth();
  const unitOptions = [...UNIT_OPTIONS];
  const { invoiceId: invoiceIdFromParams } = useParams<{ invoiceId?: string }>();
  const [editingInvoiceId, setEditingInvoiceId] = useState<string | null>(invoiceIdFromParams || null);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isFromOffer = searchParams.get('fromOffer') === 'true';
  const isFromOrder = searchParams.get('fromOrder') === 'true';
  const isFromWaybill = searchParams.get('fromWaybill') === 'true';
  const isFromReturn = searchParams.get('fromReturn') === 'true';
  const successNavTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // ✅ E-Fatura state'leri
  const [eInvoiceProfileType, _setEInvoiceProfileType] = useState<EInvoiceProfileType>(EInvoiceProfileType.TEMEL);
  const [eInvoiceTypeCode, _setEInvoiceTypeCode] = useState<EInvoiceTypeCode>(EInvoiceTypeCode.SATIS);
  const [eInvoiceScenario, _setEInvoiceScenario] = useState<EInvoiceScenario>(EInvoiceScenario.TEMEL);
  
  // ✅ Wrapper fonksiyonlar (debug için log ekledik)
  const setEInvoiceProfileType = (value: EInvoiceProfileType) => {
    console.log('📝 [STATE UPDATE] ProfileType:', eInvoiceProfileType, '->', value);
    _setEInvoiceProfileType(value);
    // Profil türü değiştiğinde senaryoyu otomatik güncelle
    const newScenario = ProfileTypeToScenarioMap[value] || EInvoiceScenario.TEMEL;
    console.log('📝 [STATE UPDATE] Scenario otomatik güncellendi:', eInvoiceScenario, '->', newScenario);
    _setEInvoiceScenario(newScenario);
  };
  
  const setEInvoiceTypeCode = (value: EInvoiceTypeCode) => {
    console.log('📝 [STATE UPDATE] TypeCode:', eInvoiceTypeCode, '->', value);
    _setEInvoiceTypeCode(value);
  };

  // ISTISNA otomasyonu: %0 KDV kalemi varsa fatura tipini otomatik ISTISNA yaparız.
  //   - istisnaAutoRef: ISTISNA'yı otomatik mi seçtik (kalem değişince geri alabilmek için)
  //   - typeManualRef : kullanıcı tipi elle değiştirdiyse otomasyona karışmayız
  const istisnaAutoRef = useRef(false);
  const typeManualRef = useRef(false);

  // Form'dan gelen kullanıcı tip değişikliği → otomasyonu devre dışı bırak
  const handleEInvoiceTypeCodeChange = (value: EInvoiceTypeCode) => {
    typeManualRef.current = true;
    istisnaAutoRef.current = false;
    setEInvoiceTypeCode(value);
  };
  
  const setEInvoiceScenario = (value: EInvoiceScenario) => {
    console.log('📝 [STATE UPDATE] Scenario:', eInvoiceScenario, '->', value);
    _setEInvoiceScenario(value);
  };

  // ✅ Cari e-Fatura mükellefiyet durumu (cari seçilince otomatik sorgulanır)
  //   null = henüz sorgulanmadı, true = e-Fatura mükellefi, false = değil (e-Arşiv)
  const [eInvoiceMukellef, setEInvoiceMukellef] = useState<boolean | null>(null);
  const [eInvoiceMukellefLoading, setEInvoiceMukellefLoading] = useState<boolean>(false);
  const [eInvoiceMukellefTitle, setEInvoiceMukellefTitle] = useState<string>('');

  // Cari VKN/TCKN için mükellef sorgusu yapıp profil/senaryoyu otomatik ayarlar.
  //   - Mükellef  → profil Ticari (E-Fatura)
  //   - Değil     → profil E-Arşiv (TEMEL/Ticari kilitli)
  const detectMukellefAndSetProfile = useCallback(async (taxNumber?: string | null) => {
    const vkn = (taxNumber || '').replace(/[^0-9]/g, '').trim();
    if (!vkn || vkn.length < 10) {
      // Geçerli VKN/TCKN yok → sorgulama yapma, durumu sıfırla
      setEInvoiceMukellef(null);
      setEInvoiceMukellefTitle('');
      return;
    }
    setEInvoiceMukellefLoading(true);
    try {
      const resp = await einvoiceApi.post(EINVOICE_API.EDM_MUKELLEF_SORGULA, { vknTckn: vkn });
      const data = resp.data;
      const isUser = !!data?.isEInvoiceUser;
      setEInvoiceMukellef(isUser);
      const title = Array.isArray(data?.users) && data.users.length > 0 ? (data.users[0]?.title || '') : '';
      setEInvoiceMukellefTitle(title);

      if (isUser) {
        // e-Fatura mükellefi → Ticari (Özlem Hanım kuralı)
        setEInvoiceProfileType(EInvoiceProfileType.TICARIFATURA);
      } else {
        // Mükellef değil → e-Arşiv
        setEInvoiceProfileType(EInvoiceProfileType.EARSIV);
      }
    } catch (err) {
      console.warn('⚠️ [E-FATURA] Mükellef sorgusu başarısız, profil değiştirilmedi:', err);
      // Sorgu başarısızsa mevcut profili koru, durumu bilinmiyor bırak
      setEInvoiceMukellef(null);
      setEInvoiceMukellefTitle('');
    } finally {
      setEInvoiceMukellefLoading(false);
    }
  }, []);
  
  // Durum değişkenleri
  // ✅ Bugünün tarihini local timezone'da al (UTC dönüşümü YAPMA!)
  const getTodayLocal = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  const [invoiceDate, setInvoiceDate] = useState<string>(getTodayLocal());
  const [dueDate, setDueDate] = useState<string>('');
  const [invoiceNumber, setInvoiceNumber] = useState<string>('');
  // PYS: belgenin bağlı olduğu proje (opsiyonel; null → projeye bağlı değil)
  const [projectId, setProjectId] = useState<number | null>(null);
  const [isItemModalOpen, setIsItemModalOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState<InvoiceItem[]>([]);
  const [lotErrorLine, setLotErrorLine] = useState<number | null>(null);
  const [lotWarnings, setLotWarnings] = useState<string[]>([]);
  // Kaydet denendi mi — boş bırakılan zorunlu sayısal alanları kırmızı göstermek için.
  const [attemptedSave, setAttemptedSave] = useState(false);
  // İndirim kolonu giriş modu (tüm kalemler için ortak): yüzde mi tutar mı
  const [discountMode, setDiscountMode] = useState<'PERCENT' | 'AMOUNT'>('PERCENT');
  const [isCopyFromDocumentModalOpen, setIsCopyFromDocumentModalOpen] = useState(false);

  const handleImportItemsFromDocument = (
    items: InvoiceItem[],
    strategy: 'append' | 'replace'
  ) => {
    if (strategy === 'replace') {
      setSelectedItems(items);
    } else {
      setSelectedItems(prev => [...prev, ...items]);
    }
  };
  const [notes, setNotes] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [originalInvoiceData, setOriginalInvoiceData] = useState<Invoice | null>(null);

  // ✅ PR3 (Invoice Seller Snapshot Refactor): Manuel satış faturası için satıcı snapshot.
  // Manuel ekranda branch yok → resolver Companies kaynağına düşer; gerekirse backend'de
  // BranchId varsa franchise kontrolü yapılır. Frontend bunu yalnızca gösterim/log için tutar.
  const [seller, setSeller] = useState<SellerSnapshot | null>(null);

  // ✅ İrsaliye ve Ödeme Bilgileri (E-Fatura için)
  const [despatchDocumentNumber, setDespatchDocumentNumber] = useState<string>('');
  const [despatchDocumentDate, setDespatchDocumentDate] = useState<string>('');
  const [paymentMethod, setPaymentMethod] = useState<string>('');
  const [iban, setIban] = useState<string>('');
  const [paymentNote, setPaymentNote] = useState<string>('');
  
  // Tevkifat ve Stopaj
  const [tevkifatRate, setTevkifatRate] = useState<string>("0");
  const [tevkifatType, setTevkifatType] = useState<string>("");
  const [showTevkifat, setShowTevkifat] = useState<boolean>(false);
  const [stopajRate, setStopajRate] = useState<number>(0);
  const [showStopaj, setShowStopaj] = useState<boolean>(false);
  
  // ✅ TEKLİF MODU İÇİN STATE'LER
  const [validUntil, setValidUntil] = useState<string>(''); // Teklif geçerlilik tarihi
  const [offerStatus, setOfferStatus] = useState<'draft' | 'pending' | 'approved' | 'rejected'>('draft');
  const [sourceOfferId, setSourceOfferId] = useState<string | null>(null); // Tekliften fatura oluşturuluyorsa
  const [sourceOfferNumber, setSourceOfferNumber] = useState<string | null>(null); // Tekliften fatura oluşturuluyorsa
  const [sourceOrderId, setSourceOrderId] = useState<string | null>(null); // Siparişten oluşturuluyorsa
  const [sourceOrderNumber, setSourceOrderNumber] = useState<string | null>(null);
  const [orderStockHandedOver, setOrderStockHandedOver] = useState(false); // Sipariş aşamasında stok işlendiyse bilgi notu
  const [sourceWaybillId, setSourceWaybillId] = useState<string | null>(null); // İrsaliyeden oluşturuluyorsa
  const [sourceWaybillNumber, setSourceWaybillNumber] = useState<string | null>(null);
  // ✅ İADE İLİŞKİSİ - gönderilmiş bir faturadan iade oluşturuluyorsa orijinal fatura referansı
  const [returnedInvoiceId, setReturnedInvoiceId] = useState<string | null>(null);
  const [returnedInvoiceNumber, setReturnedInvoiceNumber] = useState<string | null>(null);
  const [returnedInvoiceDate, setReturnedInvoiceDate] = useState<string | null>(null);
  // ✅ İADE - elle (formdan) tip IADE seçilince orijinal fatura seçici
  const [returnCandidates, setReturnCandidates] = useState<any[]>([]);
  const [loadingReturnCandidates, setLoadingReturnCandidates] = useState<boolean>(false);
  
  // ✅ İRSALİYE MODU İÇİN STATE'LER
  const [showPrices, setShowPrices] = useState<boolean>(true); // İrsaliyede fiyat görünsün mü?
  
  // ✅ SİPARİŞ MODU İÇİN STATE'LER
  const [deliveryDate, setDeliveryDate] = useState<string>(''); // Teslim tarihi (opsiyonel)
  const [orderStatus, setOrderStatus] = useState<'draft' | 'pending' | 'confirmed' | 'shipped' | 'delivered' | 'canceled'>('pending');
  const [processStock, setProcessStock] = useState<boolean>(false); // Stok işlensin mi?
  const [showStockConfirmDialog, setShowStockConfirmDialog] = useState<boolean>(false); // Stok onay dialog
  
  // ✅ ADIM 3: Hesaplamalar
  const calculations = useInvoiceCalculations({
    items: selectedItems,
    tevkifatRate,
    stopajRate,
  });
  
  const { subtotal, netSubtotal, discountNetTotal, vatTotal, sctTotal, grandTotal, vatRateTotals, tevkifatAmount, stopajAmount, payableAmount } = calculations;
  
  // ✅ ADIM 5: Cari hesap seçimi
  const accountSelection = useAccountSelection();
  const {
    accounts, selectedAccount, accountModalOpen: isAccountModalOpen, accountSearch: accountSearchTerm,
    filteredAccounts, accountsPage, accountsRowsPerPage, loadingAccounts, setSelectedAccount,
    loadAccounts, handleOpenAccountModal, handleCloseAccountModal, handleSelectAccount,
    handleAccountSearch, handleAccountsPageChange, handleAccountsRowsPerPageChange,
  } = accountSelection;
  
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [originalInvoiceTotal, setOriginalInvoiceTotal] = useState<string>('');
  const [invoiceDifference, setInvoiceDifference] = useState<number>(0);
  const [isBulkWarehouseModalOpen, setIsBulkWarehouseModalOpen] = useState<boolean>(false);
  const [bulkWarehouseId, setBulkWarehouseId] = useState<string>('');
  const [selectedWarehouseId, setSelectedWarehouseId] = useState<string | null>(null);
  const [itemsPage, setItemsPage] = useState<number>(0);
  const [itemsRowsPerPage, setItemsRowsPerPage] = useState<number>(20);
  const [itemSearchTerm, setItemSearchTerm] = useState<string>('');
  
  const { inventoryItems, loading: inventoryLoading, totalCount, loadInventoryItemsPaged } = useInventoryData(companyId, selectedWarehouseId);
  
  const [availableCurrencies, setAvailableCurrencies] = useState<{code: string, active: boolean}[]>([{ code: 'TRY', active: true }]);
  const [currency, setCurrency] = useState<string>('TRY');
  const [exchangeRate, setExchangeRate] = useState<string>('1');

  // ✅ İADE (elle yol): tip kodu iade mi?
  const isReturnTypeSelected = ['IADE', 'TEVKIFATIADE', 'YTBIADE'].includes(String(eInvoiceTypeCode).toUpperCase());

  // Seçili carinin (orijinal) faturalarını aday olarak getir (iade dışı olanlar)
  const loadReturnCandidatesForAccount = async () => {
    const accId = selectedAccount?.id;
    if (!accId) { setReturnCandidates([]); return; }
    try {
      setLoadingReturnCandidates(true);
      const url = ACCOUNTING_API.INVOICE_BY_COMPANY({ page: 1, pageSize: 200, search: selectedAccount?.companyName || '' });
      const resp = await accountingApi.get(url);
      const list: any[] = resp.data?.data?.items || resp.data?.items || resp.data?.data || [];
      const candidates = (Array.isArray(list) ? list : [])
        .filter((inv) => String(inv.accountId) === String(accId))
        // İade faturalarının kendisi aday olmamalı
        .filter((inv) => !['IADE', 'TEVKIFATIADE', 'YTBIADE'].includes(String(inv.eInvoiceTypeCode || '').toUpperCase()))
        .filter((inv) => !inv.returnedInvoiceId)
        .sort((a, b) => new Date(b.invoiceDate).getTime() - new Date(a.invoiceDate).getTime());
      setReturnCandidates(candidates);
    } catch (err) {
      console.error('❌ [IADE] Aday faturalar yüklenemedi:', err);
      setReturnCandidates([]);
    } finally {
      setLoadingReturnCandidates(false);
    }
  };

  // Elle seçilen orijinal faturayı uygula: kalan miktarla kalemleri ön-doldur + referansları ayarla
  const handleSelectReturnOriginal = async (originalId: string) => {
    if (!originalId) {
      setReturnedInvoiceId(null);
      setReturnedInvoiceNumber(null);
      setReturnedInvoiceDate(null);
      return;
    }
    try {
      const resp = await accountingApi.get(ACCOUNTING_API.INVOICE_BY_ID(originalId));
      const data = resp.data?.data || resp.data;

      // Kalan miktarları al (return-summary). Deploy edilmemişse orijinal miktara düşeriz.
      const remainingByKey: Record<string, number> = {};
      let summaryStatus: string | undefined;
      try {
        const sumResp = await accountingApi.get(ACCOUNTING_API.INVOICE_RETURN_SUMMARY(originalId));
        const summary = sumResp.data?.data || sumResp.data;
        summaryStatus = summary?.returnStatus;
        (summary?.items || []).forEach((s: any) => {
          const key = (s.itemId && !String(s.itemId).startsWith('manual_'))
            ? `id:${String(s.itemId).toLowerCase()}`
            : `name:${String(s.itemName || '').trim().toLowerCase()}`;
          remainingByKey[key] = s.remainingQuantity;
        });
      } catch {
        console.warn('⚠️ [IADE] return-summary alınamadı, orijinal miktarlar kullanılacak.');
      }

      if (summaryStatus === 'FULL') {
        setError('Bu fatura zaten tamamen iade edilmiş. Yeni iade oluşturulamaz.');
        return;
      }

      setReturnedInvoiceId(String(data.id));
      setReturnedInvoiceNumber(data.invoiceNumber ? String(data.invoiceNumber) : null);
      setReturnedInvoiceDate(data.invoiceDate ? String(data.invoiceDate) : null);
      if (data.currency) setCurrency(data.currency);

      const items: InvoiceItem[] = (data.items || [])
        .map((it: any) => {
          const key = (it.itemId && !String(it.itemId).startsWith('manual_'))
            ? `id:${String(it.itemId).toLowerCase()}`
            : `name:${String(it.itemName || '').trim().toLowerCase()}`;
          const remaining = (key in remainingByKey) ? remainingByKey[key] : it.quantity;
          return { it, remaining };
        })
        .filter(({ remaining }: { it: any; remaining: number }) => remaining > 0)
        .map(({ it, remaining }: { it: any; remaining: number }, index: number) => {
          const unitPrice = it.unitPrice || 0;
          const vatRate = it.vatRate || 0;
          const priceType = (it.priceType || 'VAT_EXCLUDED') as 'VAT_EXCLUDED' | 'VAT_INCLUDED';
          return {
            id: `return_item_${index}_${Date.now()}`,
            itemId: it.itemId || `return_item_${index}`,
            itemName: it.itemName || 'Ürün',
            quantity: remaining,
            displayQuantity: formatQuantityForDisplay(remaining),
            unit: it.unit || 'adet',
            priceType,
            enteredPrice: it.enteredPrice || unitPrice,
            unitPrice,
            unitPriceWithVat: it.unitPriceWithVat || Math.round((unitPrice * (1 + vatRate / 100)) * 100) / 100,
            vatRate,
            sctRate: it.sctRate || 0,
            discountRate: it.discountRate || 0,
            discountAmount: it.discountAmount || 0,
            lineTotal: it.lineTotal || it.netTotal || 0,
            netTotal: it.netTotal || 0,
            vatAmount: it.vatAmount || 0,
            sctAmount: it.sctAmount || 0,
            grossTotal: it.grossTotal || 0,
            warehouseId: it.warehouseId || '',
            ...readInvoiceLot(it),
          } as InvoiceItem;
        });

      if (items.length === 0) {
        setError('Seçilen faturada iade edilebilecek kalan miktar yok.');
        return;
      }
      setSelectedItems(items);
      setNotes((prev) => prev ? prev : `İade faturası — Orijinal fatura: ${data.invoiceNumber}`);
    } catch (err) {
      console.error('❌ [IADE] Orijinal fatura uygulanamadı:', err);
      setError('İade için seçilen orijinal fatura yüklenemedi.');
    }
  };

  // Tip IADE seçilince ve cari mevcutsa adayları yükle (elle yol; butonla gelende zaten dolu)
  useEffect(() => {
    if (isReturnTypeSelected && selectedAccount?.id && !isFromReturn) {
      loadReturnCandidatesForAccount();
    } else if (!isReturnTypeSelected) {
      setReturnCandidates([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReturnTypeSelected, selectedAccount?.id, isFromReturn]);

  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState<boolean>(false);
  const [isDraftModalOpen, setIsDraftModalOpen] = useState<boolean>(false);
  const [draftData, setDraftData] = useState<any>(null);
  const [showDraftSavedAlert, setShowDraftSavedAlert] = useState<boolean>(false);
  const [isPdfModalOpen, setIsPdfModalOpen] = useState<boolean>(false);
  const [isStockProcessModalOpen, setIsStockProcessModalOpen] = useState<boolean>(false);
  const [stockProcessCurrentItem, setStockProcessCurrentItem] = useState<number>(0);
  const [stockProcessTotalItems, setStockProcessTotalItems] = useState<number>(0);
  const [stockProcessSuccess, setStockProcessSuccess] = useState<boolean>(false);
  const [isQuickSaleInvoice, setIsQuickSaleInvoice] = useState<boolean>(false);
  const [quickSalePaymentType, setQuickSalePaymentType] = useState<string>('');
  
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);
  
  // ✅ TEKLİF/FATURA/İRSALİYE/SİPARİŞ MODUNA GÖRE DİNAMİK BAŞLIK VE NAVİGASYON
  const docNoun = isOfferMode
    ? { tr: 'Teklif', en: 'offer' }
    : isWaybillMode
      ? { tr: 'İrsaliye', en: 'waybill' }
      : isOrderMode
        ? { tr: 'Sipariş', en: 'order' }
        : { tr: 'Fatura', en: 'invoice' };
  const pageTitle = isOrderMode
    ? (editingInvoiceId ? tt("Satış Siparişi Düzenle", "Edit Sales Order") : tt("Yeni Satış Siparişi", "New Sales Order"))
    : isWaybillMode
      ? (editingInvoiceId ? tt("İrsaliye Düzenle", "Edit Waybill") : tt("Yeni İrsaliye", "New Waybill"))
      : isOfferMode 
        ? (editingInvoiceId ? tt("Satış Teklifi Düzenle", "Edit Sales Offer") : tt("Yeni Satış Teklifi", "New Sales Offer"))
        : (editingInvoiceId ? tt("Satış Faturası Düzenle", "Edit Sales Invoice") : tt("Yeni Satış Faturası", "New Sales Invoice"));
  const handleGoBack = () => navigate(isOrderMode ? '/siparisler' : isWaybillMode ? '/irsaliyeler' : isOfferMode ? '/teklifler' : '/fatura/satis');

  const getSuccessRedirectPath = useCallback(() => {
    if (isOrderMode) return '/siparisler';
    if (isWaybillMode) return '/irsaliyeler';
    if (isOfferMode) return '/teklifler';
    return '/fatura/satis';
  }, [isOrderMode, isWaybillMode, isOfferMode]);

  const clearSuccessNavTimer = useCallback(() => {
    if (successNavTimerRef.current) {
      clearTimeout(successNavTimerRef.current);
      successNavTimerRef.current = null;
    }
  }, []);

  const scheduleSuccessRedirect = useCallback(() => {
    clearSuccessNavTimer();
    successNavTimerRef.current = setTimeout(() => {
      successNavTimerRef.current = null;
      navigate(getSuccessRedirectPath());
    }, 1500);
  }, [clearSuccessNavTimer, navigate, getSuccessRedirectPath]);

  const handleSuccessModalClose = useCallback(() => {
    clearSuccessNavTimer();
    setIsSuccessModalOpen(false);
    navigate(getSuccessRedirectPath());
  }, [clearSuccessNavTimer, navigate, getSuccessRedirectPath]);

  useEffect(() => () => clearSuccessNavTimer(), [clearSuccessNavTimer]);

  useEffect(() => {
    const newScenario = ProfileTypeToScenarioMap[eInvoiceProfileType];
    if (newScenario && newScenario !== eInvoiceScenario) setEInvoiceScenario(newScenario);
  }, [eInvoiceProfileType]);

  // ✅ Cari değiştiğinde e-Fatura mükellefiyetini otomatik tespit et.
  //   Kapsam: yeni fatura + dönüşüm akışları (teklif/sipariş/irsaliye/iade) + manuel cari seçimi.
  //   Düzenleme (editingInvoiceId): kayıtlı profil korunur, otomatik tespit ile EZİLMEZ.
  //   E-fatura entegrasyonu yoksa sorgu sessizce başarısız olur (detect içinde yakalanır),
  //   normal fatura akışı hiçbir şekilde bloklanmaz.
  useEffect(() => {
    if (editingInvoiceId) return; // düzenlemede kayıtlı senaryoya dokunma
    const tax = selectedAccount?.taxNumber;
    if (tax && String(tax).replace(/[^0-9]/g, '').length >= 10) {
      detectMukellefAndSetProfile(tax);
    } else {
      // Cari yok / geçersiz VKN → rozeti temizle
      setEInvoiceMukellef(null);
      setEInvoiceMukellefTitle('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccount?.taxNumber, editingInvoiceId]);

  // ✅ İstisna otomasyonu: kalemlerden herhangi birinde %0 KDV varsa fatura tipi otomatik ISTISNA olur.
  //   Yalnızca SATIS tipindeyken devreye girer (IADE/TEVKIFAT gibi bilinçli seçimleri ezmez).
  //   Kullanıcı tipi elle değiştirdiyse (typeManualRef) karışmaz. Düzenlemede kayıtlı tip korunur.
  useEffect(() => {
    if (editingInvoiceId) return;          // düzenlemede kayıtlı tipe dokunma
    if (typeManualRef.current) return;     // kullanıcı elle seçtiyse karışma
    const hasZeroVat = selectedItems.some((i) => Number(i.vatRate ?? 0) === 0);
    if (hasZeroVat && eInvoiceTypeCode === EInvoiceTypeCode.SATIS) {
      setEInvoiceTypeCode(EInvoiceTypeCode.ISTISNA);
      istisnaAutoRef.current = true;
    } else if (!hasZeroVat && eInvoiceTypeCode === EInvoiceTypeCode.ISTISNA && istisnaAutoRef.current) {
      // Otomatik seçtiğimiz ISTISNA'yı, artık %0 kalem kalmadıysa geri al
      setEInvoiceTypeCode(EInvoiceTypeCode.SATIS);
      istisnaAutoRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItems, eInvoiceTypeCode, editingInvoiceId]);

  // ✅ Vade otomasyonu: cari seçilince (veya fatura tarihi değişince) carideki ödeme vadesi
  //   (Account.PaymentDueDays) fatura vade tarihine yansır: dueDate = faturaTarihi + vadeGünü.
  //   Düzenleme (editingInvoiceId): kayıtlı vade EZİLMEZ. Carinin vadesi tanımsız/0 ise mevcut
  //   değere dokunulmaz. Alan elle değiştirilebilir kalır (yeni cari/tarih seçimine kadar).
  useEffect(() => {
    if (editingInvoiceId) return;   // düzenlemede kayıtlı vadeye dokunma
    if (isQuickSaleInvoice) return; // hızlı satış faturası salt-okunur akış
    const days = Number(selectedAccount?.paymentDueDays ?? 0);
    if (!selectedAccount?.id || !Number.isFinite(days) || days <= 0) return;

    const base = new Date(`${invoiceDate || getTodayLocal()}T00:00:00`);
    if (isNaN(base.getTime())) return;
    base.setDate(base.getDate() + days);

    const y = base.getFullYear();
    const m = String(base.getMonth() + 1).padStart(2, '0');
    const d = String(base.getDate()).padStart(2, '0');
    setDueDate(`${y}-${m}-${d}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccount?.id, invoiceDate, editingInvoiceId, isQuickSaleInvoice]);

  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      setError(null);
      const uid = ownAuthSession.getCurrentUid();
      if (!uid) {
        setError('Kullanıcı oturumu bulunamadı.');
        setLoading(false);
        return;
      }
      const userData = await firebaseDataService.getUserData(uid);
      if (userData && userData.companyId) {
        const userCompanyId = userData.companyId;
        setCompanyId(userCompanyId);
        await loadAccounts(userCompanyId);
        loadCurrencies(userCompanyId);
        if (editingInvoiceId) {
          // ✅ Offer mode'da teklif, waybill mode'da irsaliye, fatura mode'da fatura yükle
          if (isOfferMode) {
            await loadOfferForEditing(editingInvoiceId, userCompanyId);
          } else if (isWaybillMode) {
            await loadWaybillForEditing(editingInvoiceId, userCompanyId);
          } else if (isOrderMode) {
            await loadOrderForEditing(editingInvoiceId, userCompanyId);
          } else {
            await loadInvoiceForEditing(editingInvoiceId, userCompanyId);
          }
        } else {
          generateInvoiceNumber(userCompanyId);
          
          // ✅ Tekliften fatura/sipariş oluşturma - verileri yükle
          if (isFromOffer && isOrderMode) {
            console.log('🔄 [OFFER→ORDER] Tekliften sipariş modu aktif, veriler yükleniyor...');
            await loadOfferDataForOrder();
          } else if (isFromOffer) {
            console.log('🔄 [OFFER] Tekliften fatura modu aktif, veriler yükleniyor...');
            await loadOfferDataFromStorage();
          }

          // ✅ Gönderilmiş bir faturadan iade faturası oluşturma
          if (isFromReturn) {
            console.log('🔄 [IADE] İade faturası modu aktif, veriler yükleniyor...');
            await loadReturnDataFromStorage();
          }
          
          // ✅ Siparişten fatura/irsaliye oluşturma
          if (isFromOrder) {
            console.log('🔄 [ORDER] Siparişten dönüşüm modu aktif, veriler yükleniyor...');
            await loadOrderDataFromStorage();
          }
          
          // ✅ İrsaliyeden fatura oluşturma
          if (isFromWaybill) {
            console.log('🔄 [WAYBILL] İrsaliyeden fatura modu aktif, veriler yükleniyor...');
            await loadWaybillDataFromStorage();
          }
          
          setLoading(false);
        }
      } else {
        setError('Kullanıcı bilgileri bulunamadı.');
        setLoading(false);
      }
    };
    
    // ✅ Tekliften gelen verileri yükle (localStorage'dan)
    const loadOfferDataFromStorage = async () => {
      // localStorage'dan teklif verisini al
      const offerDataStr = localStorage.getItem('offerToInvoice');
      if (!offerDataStr) {
        console.log('⚠️ [OFFER] localStorage\'da teklif verisi bulunamadı');
        return;
      }
      
      try {
        const offerData = JSON.parse(offerDataStr);
        localStorage.removeItem('offerToInvoice'); // Kullanıldıktan sonra temizle
        console.log('📥 [OFFER] Teklif verisi yüklendi:', offerData);
        
        // ✅ Teklif ID ve numarasını kaydet (fatura kaydederken kullanılacak)
        if (offerData.offerId) {
          setSourceOfferId(offerData.offerId);
          console.log('✅ [OFFER] Kaynak teklif ID ayarlandı:', offerData.offerId);
        }
        if (offerData.offerNumber) {
          setSourceOfferNumber(offerData.offerNumber);
          console.log('✅ [OFFER] Kaynak teklif numarası ayarlandı:', offerData.offerNumber);
        }
        
        // Cari hesabı ayarla
        if (offerData.account) {
          const accountData: Account = {
            id: offerData.accountId || '',
            companyName: offerData.account.companyName || '',
            authorizedPerson: offerData.account.authorizedPerson || '',
            taxNumber: offerData.account.taxNumber || '',
            taxOffice: offerData.account.taxOffice || '',
            address: offerData.account.address || '',
            city: offerData.account.city || '',
            district: offerData.account.district || '',
            phone: offerData.account.phone || '',
            email: offerData.account.email || '',
          };
          setSelectedAccount(accountData);
          console.log('✅ [OFFER] Cari hesap ayarlandı:', accountData.companyName);
        }
        
        // Ürünleri dönüştür ve ayarla
        if (offerData.items && offerData.items.length > 0) {
          const convertedItems: InvoiceItem[] = offerData.items.map((item: any, index: number) => {
            const itemName = item.name || item.itemName || 'Ürün';
            const quantity = item.quantity || 1;
            const unitPrice = item.unitPrice || 0;
            const enteredPrice = item.enteredPrice || unitPrice; // ✅ Orijinal girilen fiyat
            const priceType = item.priceType || 'VAT_EXCLUDED'; // ✅ Fiyat tipi
            const vatRate = item.vatRate || 0;
            const sctRate = item.sctRate || 0;
            const discountRate = item.discountRate || 0;
            
            // ✅ Tekliften gelen hesaplanmış değerleri kullan (yeniden hesaplama YAPMA!)
            // Eğer teklifte zaten hesaplanmışsa onları kullan
            // Not: Teklif API'si vatTotal olarak döndürüyor, fatura vatAmount kullanıyor
            let lineTotal = item.lineTotal || item.netTotal || 0;
            let netTotal = item.netTotal || 0;
            let vatAmount = item.vatAmount || item.vatTotal || 0; // vatTotal da kontrol et
            let grossTotal = item.grossTotal || item.totalPrice || 0; // totalPrice de kontrol et
            let discountAmount = item.discountAmount || 0;
            let sctAmount = item.sctAmount || item.sctTotal || 0; // sctTotal da kontrol et
            let unitPriceWithVat = item.unitPriceWithVat || 0;
            
            console.log('📊 [OFFER ITEM] Gelen değerler:', {
              itemName,
              quantity,
              priceType,
              enteredPrice,
              unitPrice,
              vatRate,
              netTotal,
              vatAmount,
              grossTotal,
              'item.vatTotal': item.vatTotal,
              'item.vatAmount': item.vatAmount,
              'item.netTotal': item.netTotal,
              'item.grossTotal': item.grossTotal,
              'hesaplama_yapilacak': (netTotal === 0 || grossTotal === 0)
            });
            
            // Eğer hesaplanmış değerler yoksa, frontend'de hesapla
            if (netTotal === 0 || grossTotal === 0) {
              console.log('🔄 [OFFER ITEM] Hesaplama yapılıyor...');
              if (priceType === 'VAT_INCLUDED') {
                // KDV Dahil: enteredPrice üzerinden hesapla
                const totalGross = Math.round((enteredPrice * quantity) * 100) / 100;
                discountAmount = Math.round((totalGross * (discountRate / 100)) * 100) / 100;
                const discountedGross = Math.round((totalGross - discountAmount) * 100) / 100;
                vatAmount = Math.round((discountedGross - (discountedGross / (1 + vatRate / 100))) * 100) / 100;
                netTotal = Math.round((discountedGross - vatAmount) * 100) / 100;
                sctAmount = Math.round((netTotal * (sctRate / 100)) * 100) / 100;
                lineTotal = discountRate > 0 ? Math.round((netTotal / (1 - discountRate / 100)) * 100) / 100 : netTotal;
                grossTotal = discountedGross;
              } else {
                // KDV Hariç: unitPrice üzerinden hesapla
                lineTotal = Math.round((quantity * unitPrice) * 100) / 100;
                discountAmount = Math.round((lineTotal * (discountRate / 100)) * 100) / 100;
                netTotal = Math.round((lineTotal - discountAmount) * 100) / 100;
                sctAmount = Math.round((netTotal * (sctRate / 100)) * 100) / 100;
                vatAmount = Math.round(((netTotal + sctAmount) * (vatRate / 100)) * 100) / 100;
                grossTotal = Math.round((netTotal + sctAmount + vatAmount) * 100) / 100;
              }
              // unitPriceWithVat'ı da hesapla
              unitPriceWithVat = priceType === 'VAT_INCLUDED' ? enteredPrice : Math.round((unitPrice * (1 + vatRate / 100)) * 100) / 100;
              console.log('✅ [OFFER ITEM] Hesaplama sonucu:', { netTotal, vatAmount, grossTotal, unitPriceWithVat });
            } else {
              // Hesaplama yapılmadıysa mevcut değerleri kullan veya hesapla
              console.log('⏭️ [OFFER ITEM] Hesaplama atlandı, mevcut değerler kullanılıyor');
              if (unitPriceWithVat === 0) {
                unitPriceWithVat = priceType === 'VAT_INCLUDED' ? enteredPrice : Math.round((unitPrice * (1 + vatRate / 100)) * 100) / 100;
              }
              // vatAmount hala 0 ise ve grossTotal, netTotal var ise hesapla
              if (vatAmount === 0 && grossTotal > 0 && netTotal > 0) {
                vatAmount = Math.round((grossTotal - netTotal) * 100) / 100;
                console.log('🔧 [OFFER ITEM] vatAmount hesaplandı:', vatAmount);
              }
            }
            
            return {
              id: item.id || `offer_item_${index}_${Date.now()}`,
              itemId: item.itemId || item.stockId || item.id || `offer_item_${index}`, // ✅ Stok ID'sini kullan
              itemName: itemName,
              quantity: quantity,
              displayQuantity: formatQuantityForDisplay(quantity),
              unit: item.unit || 'adet',
              priceType: priceType as 'VAT_EXCLUDED' | 'VAT_INCLUDED',
              enteredPrice: enteredPrice,
              unitPrice: unitPrice,
              unitPriceWithVat: unitPriceWithVat,
              vatRate: vatRate,
              sctRate: sctRate,
              discountRate: discountRate,
              discountAmount: discountAmount,
              lineTotal: lineTotal,
              netTotal: netTotal,
              vatAmount: vatAmount,
              sctAmount: sctAmount,
              grossTotal: grossTotal,
              warehouseId: item.warehouseId || '', // ✅ Depo ID'si - teklifte yoksa kullanıcı seçmeli
            };
          });
          
          setSelectedItems(convertedItems);
          console.log('✅ [OFFER] Ürünler aktarıldı:', convertedItems.length, 'adet', convertedItems);
        }
        
        // Notları ayarla
        if (offerData.notes) {
          setNotes(offerData.notes);
          console.log('✅ [OFFER] Notlar ayarlandı:', offerData.notes);
        }
        
        // Para birimini ayarla
        if (offerData.currency) {
          setCurrency(offerData.currency);
          console.log('✅ [OFFER] Para birimi ayarlandı:', offerData.currency);
        }

        // PYS: proje bağı belge zincirinde taşınır (teklif → fatura)
        setProjectId(offerData.projectId ?? null);
        
        console.log('🎉 [OFFER] Teklif verileri başarıyla fatura formuna aktarıldı!');
        
      } catch (err) {
        console.error('❌ [OFFER] Teklif verileri parse edilirken hata:', err);
      }
    };
    
    // ✅ Gönderilmiş bir faturadan iade faturası oluşturma - localStorage'dan veri yükle
    const loadReturnDataFromStorage = async () => {
      const raw = localStorage.getItem('returnFromInvoice');
      if (!raw) {
        console.log('⚠️ [IADE] localStorage\'da iade kaynağı bulunamadı');
        return;
      }
      try {
        const src = JSON.parse(raw);
        localStorage.removeItem('returnFromInvoice');
        console.log('📥 [IADE] İade kaynağı yüklendi:', src);

        // İade tipi olarak işaretle (E-Fatura tip kodu = IADE)
        setEInvoiceTypeCode(EInvoiceTypeCode.IADE);

        // İade edilen orijinal fatura referansı (yeni payload: returnedInvoice*, eski: id/invoiceNumber/invoiceDate)
        const refId = src.returnedInvoiceId ?? src.id;
        const refNumber = src.returnedInvoiceNumber ?? src.invoiceNumber;
        const refDate = src.returnedInvoiceDate ?? src.invoiceDate;
        if (refId) setReturnedInvoiceId(String(refId));
        if (refNumber) setReturnedInvoiceNumber(String(refNumber));
        if (refDate) setReturnedInvoiceDate(String(refDate));

        // Cari hesabı ayarla (orijinal faturadaki cari ile aynı olmalı)
        if (src.accountId) {
          setSelectedAccount({
            id: String(src.accountId),
            companyName: src.accountName || '',
            authorizedPerson: '',
            taxNumber: src.accountTaxNumber || '',
            taxOffice: src.accountTaxOffice || '',
            address: src.accountAddress || '',
            city: '',
            district: '',
            phone: src.accountPhone || '',
            email: src.accountEmail || '',
          } as Account);
        }

        // Para birimi
        if (src.currency) setCurrency(src.currency);

        // PYS: iade faturası aynı projeye bağlanır (kartlarda ters işaretle düşer)
        setProjectId(src.projectId ?? null);

        // Kalemleri kopyala (orijinal fatura kalemleri zaten hesaplanmış değerlere sahip)
        if (Array.isArray(src.items) && src.items.length > 0) {
          const items: InvoiceItem[] = src.items.map((it: any, index: number) => {
            const quantity = it.quantity || 1;
            const unitPrice = it.unitPrice || 0;
            const vatRate = it.vatRate || 0;
            const priceType = (it.priceType || 'VAT_EXCLUDED') as 'VAT_EXCLUDED' | 'VAT_INCLUDED';
            const enteredPrice = it.enteredPrice || unitPrice;
            return {
              id: `return_item_${index}_${Date.now()}`,
              itemId: it.itemId || `return_item_${index}`,
              itemName: it.itemName || 'Ürün',
              quantity,
              displayQuantity: formatQuantityForDisplay(quantity),
              unit: it.unit || 'adet',
              priceType,
              enteredPrice,
              unitPrice,
              unitPriceWithVat: it.unitPriceWithVat || Math.round((unitPrice * (1 + vatRate / 100)) * 100) / 100,
              vatRate,
              sctRate: it.sctRate || 0,
              discountRate: it.discountRate || 0,
              discountAmount: it.discountAmount || 0,
              lineTotal: it.lineTotal || it.netTotal || 0,
              netTotal: it.netTotal || 0,
              vatAmount: it.vatAmount || 0,
              sctAmount: it.sctAmount || 0,
              grossTotal: it.grossTotal || 0,
              warehouseId: it.warehouseId || '',
              ...readInvoiceLot(it),
            } as InvoiceItem;
          });
          setSelectedItems(items);
          console.log('✅ [IADE] Orijinal fatura kalemleri kopyalandı:', items.length);
        }

        // ✅ Tevkifat/Stopaj — orijinal faturadan taşı (TEVKIFATIADE için zorunlu)
        if (src.tevkifatRate && String(src.tevkifatRate) !== "0") {
          setShowTevkifat(true);
          setTevkifatRate(String(src.tevkifatRate));
          if (src.tevkifatType) setTevkifatType(String(src.tevkifatType));
          // İade + tevkifat → e-fatura tipi TEVKIFATIADE
          setEInvoiceTypeCode(EInvoiceTypeCode.TEVKIFATIADE);
        }
        if (src.stopajRate && Number(src.stopajRate) > 0) {
          setShowStopaj(true);
          setStopajRate(Number(src.stopajRate));
        }

        // Bilgilendirme notu
        if (refNumber) {
          setNotes((prev) => prev ? prev : `İade faturası — Orijinal fatura: ${refNumber}`);
        }
      } catch (err) {
        console.error('❌ [IADE] İade kaynağı parse edilemedi:', err);
      }
    };

    // ✅ Tekliften sipariş oluşturma - localStorage'dan veri yükle
    const loadOfferDataForOrder = async () => {
      const offerDataStr = localStorage.getItem('offerToOrder');
      if (!offerDataStr) {
        console.log('⚠️ [OFFER→ORDER] localStorage\'da teklif verisi bulunamadı');
        return;
      }
      
      try {
        const offerData = JSON.parse(offerDataStr);
        localStorage.removeItem('offerToOrder');
        console.log('📥 [OFFER→ORDER] Teklif verisi yüklendi:', offerData);
        
        // Kaynak teklif bilgisi
        if (offerData.offerId) setSourceOfferId(offerData.offerId);
        if (offerData.offerNumber) setSourceOfferNumber(offerData.offerNumber);
        
        // Cari hesabı ayarla
        if (offerData.account) {
          const accountData: Account = {
            id: offerData.accountId || '',
            companyName: offerData.account.companyName || '',
            authorizedPerson: offerData.account.authorizedPerson || '',
            taxNumber: offerData.account.taxNumber || '',
            taxOffice: offerData.account.taxOffice || '',
            address: offerData.account.address || '',
            city: offerData.account.city || '',
            district: offerData.account.district || '',
            phone: offerData.account.phone || '',
            email: offerData.account.email || '',
          };
          setSelectedAccount(accountData);
        }
        
        // Ürünleri dönüştür
        if (offerData.items && offerData.items.length > 0) {
          const convertedItems: InvoiceItem[] = offerData.items.map((item: any, index: number) => {
            const itemName = item.name || item.itemName || 'Ürün';
            const quantity = item.quantity || 1;
            const unitPrice = item.unitPrice || 0;
            const enteredPrice = item.enteredPrice || unitPrice;
            const priceType = item.priceType || 'VAT_EXCLUDED';
            const vatRate = item.vatRate || 0;
            const sctRate = item.sctRate || 0;
            const discountRate = item.discountRate || 0;
            
            let lineTotal = item.lineTotal || item.netTotal || 0;
            let netTotal = item.netTotal || 0;
            let vatAmount = item.vatAmount || item.vatTotal || 0;
            let grossTotal = item.grossTotal || item.totalPrice || 0;
            let discountAmount = item.discountAmount || 0;
            let sctAmount = item.sctAmount || item.sctTotal || 0;
            let unitPriceWithVat = item.unitPriceWithVat || 0;
            
            if (netTotal === 0 || grossTotal === 0) {
              if (priceType === 'VAT_INCLUDED') {
                const totalGross = Math.round((enteredPrice * quantity) * 100) / 100;
                discountAmount = Math.round((totalGross * (discountRate / 100)) * 100) / 100;
                const discountedGross = Math.round((totalGross - discountAmount) * 100) / 100;
                vatAmount = Math.round((discountedGross - (discountedGross / (1 + vatRate / 100))) * 100) / 100;
                netTotal = Math.round((discountedGross - vatAmount) * 100) / 100;
                sctAmount = Math.round((netTotal * (sctRate / 100)) * 100) / 100;
                lineTotal = discountRate > 0 ? Math.round((netTotal / (1 - discountRate / 100)) * 100) / 100 : netTotal;
                grossTotal = discountedGross;
              } else {
                lineTotal = Math.round((quantity * unitPrice) * 100) / 100;
                discountAmount = Math.round((lineTotal * (discountRate / 100)) * 100) / 100;
                netTotal = Math.round((lineTotal - discountAmount) * 100) / 100;
                sctAmount = Math.round((netTotal * (sctRate / 100)) * 100) / 100;
                vatAmount = Math.round(((netTotal + sctAmount) * (vatRate / 100)) * 100) / 100;
                grossTotal = Math.round((netTotal + sctAmount + vatAmount) * 100) / 100;
              }
              unitPriceWithVat = priceType === 'VAT_INCLUDED' ? enteredPrice : Math.round((unitPrice * (1 + vatRate / 100)) * 100) / 100;
            } else if (unitPriceWithVat === 0) {
              unitPriceWithVat = priceType === 'VAT_INCLUDED' ? enteredPrice : Math.round((unitPrice * (1 + vatRate / 100)) * 100) / 100;
            }
            
            return {
              id: item.id || `offer_order_item_${index}_${Date.now()}`,
              itemId: item.itemId || item.stockId || item.id || `offer_order_item_${index}`,
              itemName, quantity,
              displayQuantity: formatQuantityForDisplay(quantity),
              unit: item.unit || 'adet',
              priceType: priceType as 'VAT_EXCLUDED' | 'VAT_INCLUDED',
              enteredPrice, unitPrice, unitPriceWithVat,
              vatRate, sctRate, discountRate, discountAmount,
              lineTotal, netTotal, vatAmount, sctAmount, grossTotal,
              warehouseId: item.warehouseId || '',
            };
          });
          setSelectedItems(convertedItems);
        }
        
        if (offerData.notes) setNotes(offerData.notes);
        if (offerData.currency) setCurrency(offerData.currency);
        // PYS: proje bağı belge zincirinde taşınır (teklif → sipariş)
        setProjectId(offerData.projectId ?? null);
        
        console.log('🎉 [OFFER→ORDER] Teklif verileri başarıyla sipariş formuna aktarıldı!');
      } catch (err) {
        console.error('❌ [OFFER→ORDER] Teklif verileri parse edilirken hata:', err);
      }
    };
    
    // ✅ Siparişten fatura/irsaliye oluşturma - localStorage'dan veri yükle
    const loadOrderDataFromStorage = async () => {
      const orderDataStr = localStorage.getItem('orderToInvoice') || localStorage.getItem('orderToWaybill');
      if (!orderDataStr) {
        console.log('⚠️ [ORDER] localStorage\'da sipariş verisi bulunamadı');
        return;
      }
      
      try {
        const orderData = JSON.parse(orderDataStr);
        localStorage.removeItem('orderToInvoice');
        localStorage.removeItem('orderToWaybill');
        console.log('📥 [ORDER] Sipariş verisi yüklendi:', orderData);
        
        // Kaynak sipariş bilgisi
        if (orderData.orderId) setSourceOrderId(orderData.orderId);
        if (orderData.orderNumber) setSourceOrderNumber(orderData.orderNumber);
        setOrderStockHandedOver(isOrderStockProcessed(orderData));
        
        // Cari hesabı ayarla
        if (orderData.account) {
          const accountData: Account = {
            id: orderData.accountId || '',
            companyName: orderData.account.companyName || '',
            authorizedPerson: orderData.account.authorizedPerson || '',
            taxNumber: orderData.account.taxNumber || '',
            taxOffice: orderData.account.taxOffice || '',
            address: orderData.account.address || '',
            city: orderData.account.city || '',
            district: orderData.account.district || '',
            phone: orderData.account.phone || '',
            email: orderData.account.email || '',
          };
          setSelectedAccount(accountData);
        }
        
        // Ürünleri dönüştür
        if (orderData.items && orderData.items.length > 0) {
          const convertedItems: InvoiceItem[] = orderData.items.map((item: any, index: number) => {
            const itemName = item.name || item.itemName || 'Ürün';
            const quantity = item.quantity || 1;
            const unitPrice = item.unitPrice || 0;
            const enteredPrice = item.enteredPrice || unitPrice;
            const priceType = item.priceType || 'VAT_EXCLUDED';
            const vatRate = item.vatRate || 0;
            const sctRate = item.sctRate || 0;
            const discountRate = item.discountRate || 0;
            
            let lineTotal = item.lineTotal || item.netTotal || 0;
            let netTotal = item.netTotal || 0;
            let vatAmount = item.vatAmount || item.vatTotal || 0;
            let grossTotal = item.grossTotal || item.totalPrice || 0;
            let discountAmount = item.discountAmount || 0;
            let sctAmount = item.sctAmount || item.sctTotal || 0;
            let unitPriceWithVat = item.unitPriceWithVat || 0;
            
            if (netTotal === 0 || grossTotal === 0) {
              lineTotal = Math.round((quantity * unitPrice) * 100) / 100;
              discountAmount = Math.round((lineTotal * (discountRate / 100)) * 100) / 100;
              netTotal = Math.round((lineTotal - discountAmount) * 100) / 100;
              sctAmount = Math.round((netTotal * (sctRate / 100)) * 100) / 100;
              vatAmount = Math.round(((netTotal + sctAmount) * (vatRate / 100)) * 100) / 100;
              grossTotal = Math.round((netTotal + sctAmount + vatAmount) * 100) / 100;
            }
            if (unitPriceWithVat === 0) {
              unitPriceWithVat = priceType === 'VAT_INCLUDED' ? enteredPrice : Math.round((unitPrice * (1 + vatRate / 100)) * 100) / 100;
            }
            
            return {
              id: item.id || `order_item_${index}_${Date.now()}`,
              itemId: item.itemId || item.stockId || `order_item_${index}`,
              itemName, quantity,
              displayQuantity: formatQuantityForDisplay(quantity),
              unit: item.unit || 'adet',
              priceType: priceType as 'VAT_EXCLUDED' | 'VAT_INCLUDED',
              enteredPrice, unitPrice, unitPriceWithVat,
              vatRate, sctRate, discountRate, discountAmount,
              lineTotal, netTotal, vatAmount, sctAmount, grossTotal,
              warehouseId: item.warehouseId || '',
            };
          });
          setSelectedItems(convertedItems);
        }
        
        if (orderData.notes) setNotes(orderData.notes);
        if (orderData.currency) setCurrency(orderData.currency);
        // PYS: proje bağı belge zincirinde taşınır (sipariş → fatura/irsaliye)
        setProjectId(orderData.projectId ?? null);
        
        console.log('🎉 [ORDER] Sipariş verileri başarıyla forma aktarıldı!');
      } catch (err) {
        console.error('❌ [ORDER] Sipariş verileri parse edilirken hata:', err);
      }
    };
    
    // ✅ İrsaliyeden fatura oluşturma - localStorage'dan veri yükle
    const loadWaybillDataFromStorage = async () => {
      const waybillDataStr = localStorage.getItem('waybillToInvoice');
      if (!waybillDataStr) {
        console.log('⚠️ [WAYBILL] localStorage\'da irsaliye verisi bulunamadı');
        return;
      }
      
      try {
        const waybillData = JSON.parse(waybillDataStr);
        localStorage.removeItem('waybillToInvoice');
        console.log('📥 [WAYBILL] İrsaliye verisi yüklendi:', waybillData);
        
        // Kaynak irsaliye bilgisi
        if (waybillData.waybillId) setSourceWaybillId(waybillData.waybillId);
        if (waybillData.waybillNumber) setSourceWaybillNumber(waybillData.waybillNumber);
        
        // İrsaliye numarasını sevk irsaliyesi alanına yaz
        if (waybillData.waybillNumber) {
          setDespatchDocumentNumber(waybillData.waybillNumber);
        }
        if (waybillData.waybillDate) {
          setDespatchDocumentDate(waybillData.waybillDate);
        }
        
        // Cari hesabı ayarla
        if (waybillData.account) {
          const accountData: Account = {
            id: waybillData.accountId || '',
            companyName: waybillData.account.companyName || '',
            authorizedPerson: waybillData.account.authorizedPerson || '',
            taxNumber: waybillData.account.taxNumber || '',
            taxOffice: waybillData.account.taxOffice || '',
            address: waybillData.account.address || '',
            city: waybillData.account.city || '',
            district: waybillData.account.district || '',
            phone: waybillData.account.phone || '',
            email: waybillData.account.email || '',
          };
          setSelectedAccount(accountData);
        }
        
        // Ürünleri dönüştür
        if (waybillData.items && waybillData.items.length > 0) {
          const convertedItems: InvoiceItem[] = waybillData.items.map((item: any, index: number) => {
            const itemName = item.name || item.itemName || 'Ürün';
            const quantity = item.quantity || 1;
            const unitPrice = item.unitPrice || 0;
            const enteredPrice = item.enteredPrice || unitPrice;
            const priceType = item.priceType || 'VAT_EXCLUDED';
            const vatRate = item.vatRate || 0;
            const sctRate = item.sctRate || 0;
            const discountRate = item.discountRate || 0;
            
            let lineTotal = item.lineTotal || item.netTotal || 0;
            let netTotal = item.netTotal || 0;
            let vatAmount = item.vatAmount || item.vatTotal || 0;
            let grossTotal = item.grossTotal || item.totalPrice || 0;
            let discountAmount = item.discountAmount || 0;
            let sctAmount = item.sctAmount || item.sctTotal || 0;
            let unitPriceWithVat = item.unitPriceWithVat || 0;
            
            if (netTotal === 0 || grossTotal === 0) {
              lineTotal = Math.round((quantity * unitPrice) * 100) / 100;
              discountAmount = Math.round((lineTotal * (discountRate / 100)) * 100) / 100;
              netTotal = Math.round((lineTotal - discountAmount) * 100) / 100;
              sctAmount = Math.round((netTotal * (sctRate / 100)) * 100) / 100;
              vatAmount = Math.round(((netTotal + sctAmount) * (vatRate / 100)) * 100) / 100;
              grossTotal = Math.round((netTotal + sctAmount + vatAmount) * 100) / 100;
            }
            if (unitPriceWithVat === 0) {
              unitPriceWithVat = priceType === 'VAT_INCLUDED' ? enteredPrice : Math.round((unitPrice * (1 + vatRate / 100)) * 100) / 100;
            }
            
            return {
              id: item.id || `waybill_item_${index}_${Date.now()}`,
              itemId: item.itemId || item.stockId || `waybill_item_${index}`,
              itemName, quantity,
              displayQuantity: formatQuantityForDisplay(quantity),
              unit: item.unit || 'adet',
              priceType: priceType as 'VAT_EXCLUDED' | 'VAT_INCLUDED',
              enteredPrice, unitPrice, unitPriceWithVat,
              vatRate, sctRate, discountRate, discountAmount,
              lineTotal, netTotal, vatAmount, sctAmount, grossTotal,
              warehouseId: item.warehouseId || '',
            };
          });
          setSelectedItems(convertedItems);
        }
        
        if (waybillData.notes) setNotes(waybillData.notes);
        if (waybillData.currency) setCurrency(waybillData.currency);
        // PYS: proje bağı belge zincirinde taşınır (irsaliye → fatura)
        setProjectId(waybillData.projectId ?? null);
        
        console.log('🎉 [WAYBILL] İrsaliye verileri başarıyla fatura formuna aktarıldı!');
      } catch (err) {
        console.error('❌ [WAYBILL] İrsaliye verileri parse edilirken hata:', err);
      }
    };
    
    loadInitialData();
  }, [editingInvoiceId, isFromOffer, isFromOrder, isFromWaybill, isFromReturn]);

  const loadInvoiceForEditing = async (invoiceId: string, currentCompanyId: string) => {
    try {
      const effectiveCompanyId = currentCompanyId || companyId;
      if (!effectiveCompanyId) {
        console.error('[loadInvoiceForEditing] ❌ companyId yok! currentCompanyId:', currentCompanyId, 'state companyId:', companyId);
        return;
      }
      console.log('[loadInvoiceForEditing] 🔄 Fatura yükleniyor:', invoiceId, 'companyId:', effectiveCompanyId);
      const response = await accountingApi.get(ACCOUNTING_API.INVOICE_BY_ID(invoiceId));
      const result = response.data;
      if (!result.success) throw new Error('Fatura yüklenemedi');
      const sqlData = result.data;
      const data: Invoice = {
        id: sqlData.id,
        invoiceNumber: sqlData.invoiceNumber,
        // ✅ Backend'den gelen tarihi direkt kullan (timezone dönüşümü YAPMA!)
        date: sqlData.invoiceDate ? sqlData.invoiceDate.split('T')[0] : '',
        dueDate: sqlData.dueDate ? sqlData.dueDate.split('T')[0] : '',
        accountId: sqlData.accountId,
        account: { companyName: sqlData.accountName || '', taxNumber: sqlData.accountTaxNumber, taxOffice: sqlData.accountTaxOffice, address: sqlData.accountAddress, phone: sqlData.accountPhone, email: sqlData.accountEmail },
        items: (sqlData.items || []).map((item: any) => ({
          id: item.id || Date.now().toString(), itemId: item.itemId || `manual_${item.id}`, itemName: item.itemName, quantity: item.quantity,
          displayQuantity: formatQuantityForDisplay(item.quantity), unit: item.unit, 
          // ✅ Backend'den gelen priceType'ı kullan, yoksa VAT_EXCLUDED varsayılan
          priceType: item.priceType || 'VAT_EXCLUDED',
          // ✅ YENİ: Backend'den enteredPrice geliyorsa ONU kullan (yuvarlama hatası yok!)
          // enteredPrice yoksa (eski kayıtlar için), priceType'a göre hesapla
          enteredPrice: item.enteredPrice || 
            ((item.priceType === 'VAT_INCLUDED') 
              ? item.unitPrice * (1 + (item.vatRate || 0) / 100) 
              : item.unitPrice),
          unitPrice: item.unitPrice, unitPriceWithVat: item.unitPrice * (1 + (item.vatRate || 0) / 100), vatRate: item.vatRate || 0,
          sctRate: item.sctRate || 0, discountRate: item.discountRate || 0, discountAmount: item.discountAmount || 0,
          discountType: (sqlData.discountType === 'AMOUNT' ? 'AMOUNT' : 'PERCENT'), lineTotal: item.lineTotal,
          netTotal: item.netTotal, vatAmount: item.vatAmount, sctAmount: item.sctAmount || 0, grossTotal: item.grossTotal, warehouseId: item.warehouseId || undefined,
          ...readInvoiceLot(item),
        })),
        notes: sqlData.notes, currency: sqlData.currency || 'TRY', exchangeRate: sqlData.exchangeRate || 1, tevkifatRate: sqlData.tevkifatRate,
        tevkifatType: sqlData.tevkifatType, tevkifatAmount: sqlData.tevkifatAmount, stopajRate: sqlData.stopajRate, stopajAmount: sqlData.stopajAmount,
        subtotal: sqlData.subtotal, vatTotal: sqlData.vatTotal, sctTotal: sqlData.sctTotal, discountTotal: sqlData.discountTotal, grandTotal: sqlData.grandTotal, originalInvoiceTotal: sqlData.originalInvoiceTotal,
        createdAt: sqlData.createdAt || new Date().toISOString(), branchId: sqlData.branchId || null, status: sqlData.status || 'completed',
      } as Invoice;
      setOriginalInvoiceData(data);
        setInvoiceNumber(data.invoiceNumber);
        setInvoiceDate(data.date);
        setDueDate(data.dueDate || '');
        setNotes(data.notes || '');
        setCurrency(data.currency || 'TRY');
        setExchangeRate(data.exchangeRate?.toString() || '1');
        setProjectId(sqlData.projectId ?? null);
      if (data.accountId) await fetchAccountDetails(data.accountId, currentCompanyId, data);
      setDiscountMode(sqlData.discountType === 'AMOUNT' ? 'AMOUNT' : 'PERCENT');
      setSelectedItems(data.items);
      
      // ✅ E-Fatura alanlarını yükle
      if (sqlData.eInvoiceProfileType) {
        setEInvoiceProfileType(sqlData.eInvoiceProfileType as EInvoiceProfileType);
      }
      if (sqlData.eInvoiceTypeCode) {
        setEInvoiceTypeCode(sqlData.eInvoiceTypeCode as EInvoiceTypeCode);
      }
      // ✅ İADE: düzenlenen fatura bir iade faturasıysa orijinal referansları yükle (guardrail için zorunlu)
      if (sqlData.returnedInvoiceId) {
        setReturnedInvoiceId(String(sqlData.returnedInvoiceId));
        if (sqlData.returnedInvoiceNumber) setReturnedInvoiceNumber(String(sqlData.returnedInvoiceNumber));
        if (sqlData.returnedInvoiceDate) setReturnedInvoiceDate(String(sqlData.returnedInvoiceDate));
      }
      if (sqlData.eInvoiceScenario) {
        setEInvoiceScenario(sqlData.eInvoiceScenario as EInvoiceScenario);
      }

      // ✅ İrsaliye ve Ödeme bilgilerini yükle
      setDespatchDocumentNumber(sqlData.despatchDocumentNumber || '');
      // ✅ Backend'den gelen tarihi direkt kullan (timezone dönüşümü YAPMA!)
      setDespatchDocumentDate(sqlData.despatchDocumentDate ? sqlData.despatchDocumentDate.split('T')[0] : '');
      setPaymentMethod(sqlData.paymentMethod || '');
      setIban(sqlData.iban || '');
      setPaymentNote(sqlData.paymentNote || '');
      
        if (data.tevkifatRate && data.tevkifatRate !== "0") {
        setShowTevkifat(true); setTevkifatRate(data.tevkifatRate); setTevkifatType(data.tevkifatType || "");
      }
        if (data.stopajRate && data.stopajRate > 0) {
        setShowStopaj(true); setStopajRate(data.stopajRate);
      }
        const notesLower = (data.notes || '').toLowerCase();
      const isQS = notesLower.includes('hızlı satış') || notesLower.includes('pos satış') || notesLower.includes('karma ödeme');
      setIsQuickSaleInvoice(isQS);
      if (isQS) setQuickSalePaymentType(notesLower.includes('karma') ? 'Karma Ödeme' : notesLower.includes('pos') ? 'POS' : 'Nakit');
        if (data.originalInvoiceTotal) {
          setTimeout(() => {
          setOriginalInvoiceTotal(new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(data.originalInvoiceTotal!));
      setLoading(false);
        }, 1000);
      } else setLoading(false);
      console.log('[loadInvoiceForEditing] ✅ Fatura başarıyla yüklendi:', invoiceId);
    } catch (err) {
      console.error('[loadInvoiceForEditing] ❌ Fatura yükleme hatası:', err);
      setError('Fatura bilgileri yüklenirken hata oluştu.'); navigate('/fatura/satis'); setLoading(false);
    }
  };

  // ✅ TEKLİF DÜZENLEME İÇİN VERİ YÜKLEME
  const loadOfferForEditing = async (offerId: string, currentCompanyId: string) => {
    try {
      console.log('📤 [TEKLİF] Teklif yükleniyor:', offerId);
      const response = await accountingApi.get(ACCOUNTING_API.OFFER_BY_ID(offerId));
      const result = response.data;
      console.log('📥 [TEKLİF] Yükleme yanıtı:', result);
      
      if (!result.success) throw new Error('Teklif yüklenemedi');
      const sqlData = result.data;
      
      // Debug: API'den dönen tüm field'ları göster
      console.log('🔍 [DEBUG] sqlData keys:', Object.keys(sqlData));
      console.log('🔍 [DEBUG] sqlData.items:', sqlData.items);
      console.log('🔍 [DEBUG] sqlData.Items:', sqlData.Items);
      
      // Form alanlarını doldur
      setInvoiceNumber(sqlData.offerNumber || '');
      setInvoiceDate(sqlData.offerDate ? sqlData.offerDate.split('T')[0] : '');
      setValidUntil(sqlData.validUntil ? sqlData.validUntil.split('T')[0] : '');
      setOfferStatus(sqlData.status || 'pending');
      setNotes(sqlData.notes || '');
      setProjectId(sqlData.projectId ?? null);
      setCurrency(sqlData.currency || 'TRY');
      setExchangeRate(sqlData.exchangeRate?.toString() || '1');
      
      // Cari bilgilerini doldur
      if (sqlData.accountId) {
        const accountData: Account = {
          id: sqlData.accountId,
          companyName: sqlData.accountName || '',
          taxNumber: sqlData.accountTaxNumber || '',
          taxOffice: sqlData.accountTaxOffice || '',
          address: sqlData.accountAddress || '',
          phone: sqlData.accountPhone || '',
          email: sqlData.accountEmail || '',
          authorizedPerson: '',
          city: '',
          district: '',
        };
        setSelectedAccount(accountData);
      }
      
      // Kalem bilgilerini doldur - API camelCase veya PascalCase dönebilir
      const itemsData = sqlData.items || sqlData.Items || [];
      console.log('🔍 [DEBUG] itemsData:', itemsData);
      const items = itemsData.map((item: any) => ({
        id: item.id || Date.now().toString(),
        itemId: item.itemId || `manual_${item.id}`,
        itemName: item.itemName,
        quantity: item.quantity,
        displayQuantity: formatQuantityForDisplay(item.quantity),
        unit: item.unit,
        priceType: item.priceType || 'VAT_EXCLUDED',
        enteredPrice: item.enteredPrice || item.unitPrice,
        unitPrice: item.unitPrice,
        unitPriceWithVat: item.unitPrice * (1 + (item.vatRate || 0) / 100),
        vatRate: item.vatRate || 0,
        sctRate: item.sctRate || 0,
        discountRate: item.discountRate || 0,
        discountAmount: item.discountAmount || 0,
        lineTotal: item.lineTotal,
        netTotal: item.netTotal,
        vatAmount: item.vatAmount,
        sctAmount: item.sctAmount || 0,
        grossTotal: item.grossTotal,
        warehouseId: item.warehouseId || undefined,
      }));
      setSelectedItems(items);
      
      // Tevkifat
      if (sqlData.tevkifatRate && sqlData.tevkifatRate !== "0") {
        setShowTevkifat(true);
        setTevkifatRate(sqlData.tevkifatRate);
        setTevkifatType(sqlData.tevkifatType || "");
      }
      
      // Stopaj
      if (sqlData.stopajRate && sqlData.stopajRate > 0) {
        setShowStopaj(true);
        setStopajRate(sqlData.stopajRate);
      }
      
      setLoading(false);
      console.log('✅ [TEKLİF] Teklif başarıyla yüklendi');
    } catch (err) {
      console.error('❌ [TEKLİF] Teklif yüklenirken hata:', err);
      setError('Teklif bilgileri yüklenirken hata oluştu.');
      navigate('/teklifler');
      setLoading(false);
    }
  };

  // ✅ İRSALİYE DÜZENLEME İÇİN VERİ YÜKLEME
  const loadWaybillForEditing = async (waybillId: string, currentCompanyId: string) => {
    try {
      console.log('📤 [İRSALİYE] İrsaliye yükleniyor:', waybillId);
      const response = await accountingApi.get(ACCOUNTING_API.WAYBILL_BY_ID(waybillId));
      const result = response.data;
      console.log('📥 [İRSALİYE] Yükleme yanıtı:', result);
      
      if (!result.success) throw new Error('İrsaliye yüklenemedi');
      const sqlData = result.data;
      
      // Form alanlarını doldur
      setInvoiceNumber(sqlData.waybillNumber || '');
      setInvoiceDate(sqlData.waybillDate ? sqlData.waybillDate.split('T')[0] : '');
      setNotes(sqlData.notes || '');
      setProjectId(sqlData.projectId ?? null);
      setCurrency(sqlData.currency || 'TRY');
      setExchangeRate(sqlData.exchangeRate?.toString() || '1');
      setShowPrices(sqlData.showPrices ?? true);
      
      // Cari bilgilerini doldur
      if (sqlData.accountId) {
        const accountData: Account = {
          id: sqlData.accountId,
          companyName: sqlData.accountName || '',
          taxNumber: sqlData.accountTaxNumber || '',
          taxOffice: sqlData.accountTaxOffice || '',
          address: sqlData.accountAddress || '',
          phone: sqlData.accountPhone || '',
          email: sqlData.accountEmail || '',
          authorizedPerson: '',
          city: '',
          district: '',
        };
        setSelectedAccount(accountData);
      }
      
      // Kalem bilgilerini doldur
      const itemsData = sqlData.items || sqlData.Items || [];
      const items = itemsData.map((item: any) => ({
        id: item.id || Date.now().toString(),
        itemId: item.stockId || `manual_${item.id}`,
        itemName: item.itemName,
        quantity: item.quantity,
        displayQuantity: formatQuantityForDisplay(item.quantity),
        unit: item.unit,
        priceType: 'VAT_EXCLUDED',
        enteredPrice: item.unitPrice,
        unitPrice: item.unitPrice,
        unitPriceWithVat: item.unitPrice * (1 + (item.vatRate || 0) / 100),
        vatRate: item.vatRate || 0,
        sctRate: 0,
        discountRate: 0,
        discountAmount: 0,
        lineTotal: item.netTotal,
        netTotal: item.netTotal,
        vatAmount: item.grossTotal - item.netTotal,
        sctAmount: 0,
        grossTotal: item.grossTotal,
        warehouseId: undefined,
      }));
      setSelectedItems(items);
      
      setLoading(false);
      console.log('✅ [İRSALİYE] İrsaliye başarıyla yüklendi');
    } catch (err) {
      console.error('❌ [İRSALİYE] İrsaliye yüklenirken hata:', err);
      setError('İrsaliye bilgileri yüklenirken hata oluştu.');
      navigate('/irsaliyeler');
      setLoading(false);
    }
  };

  // ✅ SİPARİŞ DÜZENLEME İÇİN VERİ YÜKLEME
  const loadOrderForEditing = async (orderId: string, currentCompanyId: string) => {
    try {
      console.log('📤 [SİPARİŞ] Sipariş yükleniyor:', orderId);
      const response = await accountingApi.get(ACCOUNTING_API.ORDER_BY_ID(orderId));
      const result = response.data;
      console.log('📥 [SİPARİŞ] Yükleme yanıtı:', result);
      
      if (!result.success) throw new Error('Sipariş yüklenemedi');
      const sqlData = result.data;
      
      // Form alanlarını doldur
      setInvoiceNumber(sqlData.orderNumber || '');
      setInvoiceDate(sqlData.orderDate ? sqlData.orderDate.split('T')[0] : '');
      setDeliveryDate(sqlData.deliveryDate ? sqlData.deliveryDate.split('T')[0] : '');
      setOrderStatus(sqlData.status || 'pending');
      setProcessStock(sqlData.processStock || false);
      setNotes(sqlData.notes || '');
      setProjectId(sqlData.projectId ?? null);
      setCurrency(sqlData.currency || 'TRY');
      setExchangeRate(sqlData.exchangeRate?.toString() || '1');
      setPaymentMethod(sqlData.paymentMethod || '');
      setIban(sqlData.iban || '');
      setPaymentNote(sqlData.paymentNote || '');
      
      // Cari bilgilerini doldur
      if (sqlData.accountId) {
        const accountData: Account = {
          id: sqlData.accountId,
          companyName: sqlData.accountName || '',
          taxNumber: sqlData.accountTaxNumber || '',
          taxOffice: sqlData.accountTaxOffice || '',
          address: sqlData.accountAddress || '',
          phone: sqlData.accountPhone || '',
          email: sqlData.accountEmail || '',
          authorizedPerson: '',
          city: '',
          district: '',
        };
        setSelectedAccount(accountData);
      }
      
      // Kalem bilgilerini doldur
      const itemsData = sqlData.items || sqlData.Items || [];
      const items = itemsData.map((item: any) => ({
        id: item.id || Date.now().toString(),
        itemId: item.itemId || `manual_${item.id}`,
        itemName: item.itemName,
        quantity: item.quantity,
        displayQuantity: formatQuantityForDisplay(item.quantity),
        unit: item.unit,
        priceType: item.priceType || 'VAT_EXCLUDED',
        enteredPrice: item.enteredPrice || item.unitPrice,
        unitPrice: item.unitPrice,
        unitPriceWithVat: item.unitPrice * (1 + (item.vatRate || 0) / 100),
        vatRate: item.vatRate || 0,
        sctRate: item.sctRate || 0,
        discountRate: item.discountRate || 0,
        discountAmount: item.discountAmount || 0,
        lineTotal: item.lineTotal,
        netTotal: item.netTotal,
        vatAmount: item.vatAmount,
        sctAmount: item.sctAmount || 0,
        grossTotal: item.grossTotal,
        warehouseId: item.warehouseId || undefined,
      }));
      setSelectedItems(items);
      
      // Tevkifat
      if (sqlData.tevkifatRate && sqlData.tevkifatRate !== "0") {
        setShowTevkifat(true);
        setTevkifatRate(sqlData.tevkifatRate);
        setTevkifatType(sqlData.tevkifatType || "");
      }
      
      // Stopaj
      if (sqlData.stopajRate && sqlData.stopajRate > 0) {
        setShowStopaj(true);
        setStopajRate(sqlData.stopajRate);
      }
      
      setLoading(false);
      console.log('✅ [SİPARİŞ] Sipariş başarıyla yüklendi');
    } catch (err) {
      console.error('❌ [SİPARİŞ] Sipariş yüklenirken hata:', err);
      setError('Sipariş bilgileri yüklenirken hata oluştu.');
      navigate('/siparisler');
      setLoading(false);
    }
  };

  const fetchAccountDetails = async (accountId: string, companyId: string, invoiceData: Invoice) => {
    try {
      const response = await accountingApi.get(ACCOUNTING_API.ACCOUNT_BY_COMPANY);
      {
        const result = response.data;
        const acc = (result.data || []).find((a: any) => a.id?.toString() === accountId || a.firebaseId === accountId);
        if (acc) {
          setSelectedAccount({
            id: acc.firebaseId || acc.id?.toString(), companyName: acc.companyName || invoiceData.account.companyName, authorizedPerson: acc.authorizedPerson || '',
            taxNumber: acc.taxNumber || invoiceData.account.taxNumber, taxOffice: acc.taxOffice || invoiceData.account.taxOffice, address: acc.address || invoiceData.account.address,
            city: acc.city || '', district: acc.district || '', phone: acc.phone || invoiceData.account.phone || '', email: acc.email || invoiceData.account.email || '',
            debitBalance: acc.debitBalance || 0, creditBalance: acc.creditBalance || 0, balance: acc.balance || 0, calculatedBalance: acc.calculatedBalance || 0,
          } as Account);
          return;
        }
      }
      setSelectedAccount({ id: invoiceData.accountId, companyName: invoiceData.account.companyName, taxNumber: invoiceData.account.taxNumber, taxOffice: invoiceData.account.taxOffice, address: invoiceData.account.address, phone: invoiceData.account.phone, email: invoiceData.account.email } as any);
    } catch (err) { console.error(err); }
  };

  const generateInvoiceNumber = async (cid: string) => {
    try {
      const response = await accountingApi.get(`${ACCOUNTING_API.INVOICE_PREVIEW_NUMBER}/${cid}/SALES?companyName=${encodeURIComponent(companyName || '')}`);
      const result = response.data;
      if (result.success && result.invoiceNumber) setInvoiceNumber(result.invoiceNumber);
    } catch (err) { console.error(err); }
  };

  const handleAddItem = (item: InventoryItem, targetWhId?: string) => {
    const vatRate = item.vatRate || 0;
    const price = item.salePrice || 0;
    const priceType = item.vatStatus === 'included' ? 'VAT_INCLUDED' : 'VAT_EXCLUDED';
    const whId = targetWhId || selectedWarehouseId || item.warehouseStocks?.[0]?.warehouseId || undefined;
    const existingIdx = selectedItems.findIndex(si => si.itemId === item.id && si.warehouseId === whId);
    if (existingIdx >= 0) {
      setSelectedItems(selectedItems.map((si, i) => i === existingIdx ? recalculateLineItem({ ...si, quantity: si.quantity + 1, displayQuantity: formatQuantityForDisplay(si.quantity + 1) }) : si));
    } else {
      setSelectedItems([...selectedItems, recalculateLineItem({
        id: Date.now().toString(), itemId: item.id, itemName: item.name, quantity: 1, displayQuantity: '1', unit: item.unit || 'adet',
        enteredPrice: price, priceType, unitPrice: 0, unitPriceWithVat: 0, vatRate, sctRate: item.sctRate || 0, discountRate: 0, discountAmount: 0, warehouseId: whId,
        lineTotal: 0, netTotal: 0, vatAmount: 0, sctAmount: 0, grossTotal: 0,
        ...lotFlagsFromItem(item),
      })]);
    }
    setIsItemModalOpen(false);
  };
  
  // Barkod okutma: koli/paket barkodu satırı ana birimde açar (1 koli = 24 adet, fiyat koli fiyatından).
  const handleBarcodeScanned = (scanned: ScannedInvoiceItem) => {
    setSelectedItems(prev => addScannedLine(prev, scanned, { warehouseId: selectedWarehouseId, priceSource: 'sale' }));
  };

  // ✅ EKSİK OLAN FONKSİYON EKLENDİ
  const handleAddManualItem = () => {
    const initialQuantity = 1;
    const defaultWhId = warehouses.find(w => w.isDefault)?.id || (warehouses.length > 0 ? warehouses[0].id : undefined);
    const newItem: InvoiceItem = {
      id: 'manual_' + Date.now().toString(),
      itemId: 'manual_item',
      itemName: '',
      quantity: initialQuantity,
      displayQuantity: formatQuantityForDisplay(initialQuantity),
      unit: 'adet',
      enteredPrice: 0,
      priceType: 'VAT_EXCLUDED',
      unitPrice: 0,
      unitPriceWithVat: 0,
      vatRate: 20,
      sctRate: 0,
      discountRate: 0,
      discountAmount: 0,
      warehouseId: defaultWhId,
      lineTotal: 0, netTotal: 0, vatAmount: 0, sctAmount: 0, grossTotal: 0
    };
    setSelectedItems([...selectedItems, recalculateLineItem(newItem)]);
  };

  const handleRemoveItem = (id: string) => setSelectedItems(selectedItems.filter(i => i.id !== id));
  const handleLotChange = (id: string, patch: Partial<InvoiceItem>) =>
    setSelectedItems(prev => prev.map(i => (i.id === id ? { ...i, ...patch } : i)));
  const handleSplitLot = (id: string) => {
    setSelectedItems(prev => {
      const index = prev.findIndex(i => i.id === id);
      if (index < 0) return prev;
      const item = prev[index];
      const half = Math.round((item.quantity / 2) * 10000) / 10000;
      const rest = Math.round((item.quantity - half) * 10000) / 10000;
      if (!(rest > 0)) return prev;
      const kept = recalculateLineItem({ ...item, quantity: half, displayQuantity: formatQuantityForDisplay(half) });
      const copy = recalculateLineItem({
        ...item,
        id: `${id}_lot_${Date.now()}`,
        quantity: rest,
        displayQuantity: formatQuantityForDisplay(rest),
        lotNo: '',
      });
      const next = [...prev];
      next.splice(index, 1, kept, copy);
      return next;
    });
  };
  const handleQuantityChange = (id: string, v: number) => setSelectedItems(selectedItems.map(i => i.id === id ? recalculateLineItem({ ...i, quantity: v }) : i));
  const handlePriceTypeChange = (id: string, v: 'VAT_EXCLUDED' | 'VAT_INCLUDED') => setSelectedItems(selectedItems.map(i => i.id === id ? recalculateLineItem({ ...i, priceType: v }) : i));
  // Sayısal alanlar: kullanıcı yazarken yalnızca metin güncellenir, sayıya çevirme
  // odak çıkışında/Enter'da yapılır. Böylece alan boş bırakılabilir.
  const handleItemDisplayChange = (id: string, field: EditableNumberField, raw: string) =>
    setSelectedItems(prev => prev.map(i => (i.id === id ? setItemDisplay(i, field, raw) : i)));
  const handleItemNumberCommit = (id: string, field: EditableNumberField) =>
    setSelectedItems(prev => prev.map(i => (i.id === id ? commitItemNumber(i, field) : i)));
  // Kolon başlığından mod değişimi → tüm kalemlere uygulanır ve yeniden hesaplanır.
  // Mevcut indirim oranları/tutarları satırlarda saklı kaldığı için mod değişince efektif indirim korunur.
  const handleDiscountModeChange = (mode: 'PERCENT' | 'AMOUNT') => {
    setDiscountMode(mode);
    // Mod değişince iki indirim alanının metinleri geçersizleşir (biri diğerinden
    // türetiliyor); sıfırlanıp sayısal değerden yeniden üretilirler.
    setSelectedItems(prev => prev.map(i => recalculateLineItem(clearDiscountDisplays({ ...i, discountType: mode }))));
  };
  const handleItemNameChange = (id: string, v: string) => setSelectedItems(selectedItems.map(i => i.id === id ? { ...i, itemName: v } : i));
  const handleUnitChange = (id: string, v: string) => setSelectedItems(selectedItems.map(i => i.id === id ? { ...i, unit: v } : i));
  
  const handleTevkifatTypeChange = (e: SelectChangeEvent<string>) => {
    const code = e.target.value; setTevkifatType(code);
    if (code) { const t = tevkifatTypes.find(x => x.code === code); if (t) setTevkifatRate(t.rate); }
  };
  const handleTevkifatRateChange = (e: SelectChangeEvent) => setTevkifatRate(e.target.value);
  const handleStopajRateChange = (e: React.ChangeEvent<HTMLInputElement>) => setStopajRate(parseFloat(e.target.value) || 0);
  const handleRemoveTevkifat = () => { setShowTevkifat(false); setTevkifatRate("0"); setTevkifatType(""); };
  const handleRemoveStopaj = () => { setShowStopaj(false); setStopajRate(0); };
  
  const handleSaveInvoice = async (stockChoice?: boolean) => {
    setAttemptedSave(true);
    if (!selectedAccount || selectedItems.length === 0) { setError(tt('Lütfen cari ve en az bir ürün seçin.', 'Please select an account and at least one item.')); return; }

    // Boş bırakılan zorunlu sayısal alanlar: kayıt engellenir, hangi kalemde hangi
    // alanın eksik olduğu tek tek bildirilir.
    const emptyFieldReports = findEmptyRequiredFields(selectedItems);
    if (emptyFieldReports.length > 0) {
      const fieldLabels: Record<string, string> = {
        quantity: tt('miktar', 'quantity'),
        price: tt('fiyat', 'price'),
        vatRate: tt('KDV oranı', 'VAT rate'),
      };
      const details = emptyFieldReports
        .map(r => `${r.index}. ${tt('kalem', 'item')} (${r.itemName || tt('adsız ürün', 'unnamed item')}): ${r.fields.map(f => fieldLabels[f]).join(', ')}`)
        .join(' • ');
      setError(`${tt('Boş bırakılan alanlar var. Lütfen doldurun:', 'There are empty fields. Please fill them in:')} ${details}`);
      return;
    }

    // ✅ İADE: tip kodu iade ise orijinal fatura zorunlu (backend de ayrıca enforce eder)
    if (!isOfferMode && !isWaybillMode && !isOrderMode && isReturnTypeSelected && !returnedInvoiceId) {
      setError(tt('İade faturası için iade edilen orijinal faturayı seçmelisiniz.', 'You must select the original invoice for a return invoice.'));
      return;
    }
    
    // ✅ TEKLİF MODUNDA FARKLI VALİDASYON
    if (isOfferMode && !validUntil) { setError(tt('Lütfen teklif geçerlilik tarihini girin.', 'Please enter offer validity date.')); return; }
    
    try {
      setLoading(true); setError(null);
      
      // ✅ SİPARİŞ MODUNDA STOK ONAY DİALOGU
      // Düzenleme modunda stok sorusu SORULMAZ, doğrudan kaydet
      if (isOrderMode && stockChoice === undefined) {
        if (editingInvoiceId) {
          stockChoice = false;
        } else {
          setShowStockConfirmDialog(true);
          setLoading(false);
          return;
        }
      }
      
      // stockChoice parametresini state'e de yansıt (UI tutarlılığı için)
      if (stockChoice !== undefined) setProcessStock(stockChoice);
      
      // ✅ TEKLİF/İRSALİYE/SİPARİŞ MODUNDA STOK İŞLEM MODAL'I GÖSTERME
      if (!isOfferMode && !isWaybillMode && !isOrderMode) {
        setStockProcessTotalItems(selectedItems.length); setStockProcessCurrentItem(0); setStockProcessSuccess(false); setIsStockProcessModalOpen(true);
      }
      
      // ✅ E-Fatura değerlerini konsola logla
      console.log('🔍 [E-FATURA STATE] ProfileType:', eInvoiceProfileType, 'TypeCode:', eInvoiceTypeCode, 'Scenario:', eInvoiceScenario);
      
      // ✅ Cari bilgilerini logla (kaydetme öncesi)
      console.log('🔍 [CARİ STATE] Kaydetme öncesi selectedAccount:', {
        companyName: selectedAccount.companyName,
        taxNumber: selectedAccount.taxNumber,
        taxOffice: selectedAccount.taxOffice,
        address: selectedAccount.address,
        phone: selectedAccount.phone,
        email: selectedAccount.email,
      });
      
      // ✅ Tarihleri local timezone'da YYYY-MM-DD formatına çevir (UTC dönüşümü olmadan)
      const formatDateOnly = (dateString: string) => {
        if (!dateString) return null;
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      // ✅ TEKLİF MODUNDA FARKLI DTO VE ENDPOINT
      if (isOfferMode) {
        const offerDto = {
          companyId, 
          projectId,
          companyName: companyName || 'DEFAULT', 
          offerType: 'SALES_OFFER',
          accountId: selectedAccount.id.toString(), // Backend string bekliyor
          accountName: selectedAccount.companyName || null,
          accountTaxNumber: selectedAccount.taxNumber || null,
          accountTaxOffice: selectedAccount.taxOffice || null,
          accountAddress: selectedAccount.address || null,
          accountPhone: selectedAccount.phone || null,
          accountEmail: selectedAccount.email || null,
          offerDate: formatDateOnly(invoiceDate),
          validUntil: formatDateOnly(validUntil),
          status: offerStatus,
          currency, 
          exchangeRate: parseFloat(exchangeRate),
          notes: notes || null,
          description: `Satış Teklifi - ${selectedAccount.companyName}`, 
          createdBy: ownAuthSession.getCurrentEmail() || 'System',
          items: selectedItems.map((item, idx) => ({
            itemId: item.itemId.startsWith('manual_') ? null : item.itemId.toString(), // Backend string bekliyor
            itemName: item.itemName,
            isManualItem: item.itemId.startsWith('manual_'),
            quantity: item.quantity, 
            unit: item.unit, 
            unitPrice: item.unitPrice, 
            enteredPrice: item.enteredPrice,
            priceType: item.priceType || 'VAT_EXCLUDED',
            vatRate: item.vatRate, 
            sctRate: item.sctRate || 0,
            discountRate: item.discountRate || 0, 
            discountAmount: item.discountAmount || 0, 
            warehouseId: item.warehouseId ? item.warehouseId.toString() : null, // Backend string bekliyor
            lineNumber: idx + 1
          }))
        };
        
        const offerUrl = editingInvoiceId 
          ? ACCOUNTING_API.OFFER_UPDATE
          : ACCOUNTING_API.OFFER_CREATE;
        
        console.log('📤 [TEKLİF] Gönderilen DTO:', offerDto);
        console.log('📤 [TEKLİF] URL:', offerUrl);
        const res = await accountingApi.post(offerUrl, editingInvoiceId ? { id: editingInvoiceId, ...offerDto } : offerDto);
        const result = res.data;
        
        console.log('📥 [TEKLİF] Backend yanıtı:', result);
        if (!result.success) throw new Error(result.message || 'Teklif kaydedilemedi');
        clearDraftFromLocalStorage(); 
        setIsSuccessModalOpen(true);
        scheduleSuccessRedirect();
      } else if (isWaybillMode) {
        // ✅ İRSALİYE MODU
        const waybillDto = {
          companyId, 
          projectId,
          companyName: companyName || 'DEFAULT', 
          accountId: selectedAccount.id.toString(),
          accountName: selectedAccount.companyName || null,
          accountTaxNumber: selectedAccount.taxNumber || null,
          accountTaxOffice: selectedAccount.taxOffice || null,
          accountAddress: selectedAccount.address || null,
          accountPhone: selectedAccount.phone || null,
          accountEmail: selectedAccount.email || null,
          waybillDate: formatDateOnly(invoiceDate),
          currency, 
          exchangeRate: parseFloat(exchangeRate) || 1,
          showPrices: showPrices,
          notes: notes || null,
          createdBy: ownAuthSession.getCurrentEmail() || 'System',
          // ✅ Sipariş ilişkisi (siparişten irsaliye oluşturuluyorsa)
          sourceOrderId: sourceOrderId || null,
          sourceOrderNumber: sourceOrderNumber || null,
          items: selectedItems.map((item, idx) => ({
            stockId: item.itemId.startsWith('manual_') ? null : item.itemId.toString(),
            itemName: item.itemName,
            quantity: item.quantity, 
            unit: item.unit, 
            unitPrice: item.unitPrice, 
            vatRate: item.vatRate,
            lineNumber: idx + 1
          }))
        };
        
        const waybillUrl = editingInvoiceId 
          ? ACCOUNTING_API.WAYBILL_UPDATE
          : ACCOUNTING_API.WAYBILL_CREATE;
        
        console.log('📤 [İRSALİYE] Gönderilen DTO:', waybillDto);
        console.log('📤 [İRSALİYE] URL:', waybillUrl);
        const res = await accountingApi.post(waybillUrl, editingInvoiceId ? { id: editingInvoiceId, ...waybillDto } : waybillDto);
        const result = res.data;
        
        console.log('📥 [İRSALİYE] Backend yanıtı:', result);
        if (!result.success) throw new Error(result.message || 'İrsaliye kaydedilemedi');
        clearDraftFromLocalStorage(); 
        setIsSuccessModalOpen(true);
        scheduleSuccessRedirect();
      } else if (isOrderMode) {
        // ✅ SİPARİŞ MODU
        const orderDto = {
          companyId, 
          projectId,
          companyName: companyName || 'DEFAULT', 
          orderType: 'SALES_ORDER',
          accountId: selectedAccount.id.toString(),
          accountName: selectedAccount.companyName || null,
          accountTaxNumber: selectedAccount.taxNumber || null,
          accountTaxOffice: selectedAccount.taxOffice || null,
          accountAddress: selectedAccount.address || null,
          accountPhone: selectedAccount.phone || null,
          accountEmail: selectedAccount.email || null,
          orderDate: formatDateOnly(invoiceDate),
          deliveryDate: deliveryDate ? formatDateOnly(deliveryDate) : null,
          processStock: stockChoice ?? false,
          status: orderStatus,
          currency, 
          exchangeRate: parseFloat(exchangeRate),
          tevkifatRate: tevkifatRate !== "0" ? tevkifatRate : null,
          tevkifatType: tevkifatType || null,
          stopajRate: stopajRate || null,
          notes: notes || null,
          description: `Satış Siparişi - ${selectedAccount.companyName}`, 
          createdBy: ownAuthSession.getCurrentEmail() || 'System',
          paymentMethod: paymentMethod || null,
          iban: iban || null,
          paymentNote: paymentNote || null,
          // ✅ Teklif ilişkisi (tekliften sipariş oluşturuluyorsa)
          sourceOfferId: sourceOfferId || null,
          sourceOfferNumber: sourceOfferNumber || null,
          items: selectedItems.map((item, idx) => ({
            itemId: item.itemId.startsWith('manual_') ? null : item.itemId.toString(),
            itemName: item.itemName,
            isManualItem: item.itemId.startsWith('manual_'),
            quantity: item.quantity, 
            unit: item.unit, 
            unitPrice: item.unitPrice, 
            enteredPrice: item.enteredPrice,
            priceType: item.priceType || 'VAT_EXCLUDED',
            vatRate: item.vatRate, 
            sctRate: item.sctRate || 0,
            discountRate: item.discountRate || 0, 
            discountAmount: item.discountAmount || 0, 
            warehouseId: item.warehouseId ? item.warehouseId.toString() : null,
            lineNumber: idx + 1
          }))
        };
        
        const orderUrl = editingInvoiceId 
          ? ACCOUNTING_API.ORDER_UPDATE
          : ACCOUNTING_API.ORDER_CREATE;
        
        console.log('📤 [SİPARİŞ] Gönderilen DTO:', orderDto);
        console.log('📤 [SİPARİŞ] URL:', orderUrl);
        const res = await accountingApi.post(orderUrl, editingInvoiceId ? { id: editingInvoiceId, ...orderDto } : orderDto);
        const result = res.data;
        
        console.log('📥 [SİPARİŞ] Backend yanıtı:', result);
        if (!result.success) throw new Error(result.message || 'Sipariş kaydedilemedi');
        clearDraftFromLocalStorage(); 
        setIsSuccessModalOpen(true);
        scheduleSuccessRedirect();
      } else {
        // ✅ FATURA MODU - ESKİ KOD
        const dto = {
          companyId, projectId, companyName: companyName || 'DEFAULT', manualInvoiceNumber: invoiceNumber.trim() || null, invoiceType: 'SALES',
          // ✅ PR3 (Invoice Seller Snapshot Refactor): Manuel akış → backend Companies fallback'e düşer
          screenType: 'Manual',
          accountId: selectedAccount.id.toString(),
          accountName: selectedAccount.companyName || null,
          accountTaxNumber: selectedAccount.taxNumber || null,
          accountTaxOffice: selectedAccount.taxOffice || null,
          accountAddress: selectedAccount.address || null,
          accountPhone: selectedAccount.phone || null,
          accountEmail: selectedAccount.email || null,
          invoiceDate: formatDateOnly(invoiceDate),
          dueDate: dueDate ? formatDateOnly(dueDate) : null,
          despatchDocumentNumber: despatchDocumentNumber || null,
          despatchDocumentDate: despatchDocumentDate ? formatDateOnly(despatchDocumentDate) : null,
          paymentMethod: paymentMethod || null,
          iban: iban || null,
          paymentNote: paymentNote || null,
          currency, exchangeRate: parseFloat(exchangeRate),
          discountType: discountMode,
          tevkifatRate: tevkifatRate !== "0" ? tevkifatRate : null, tevkifatType: tevkifatType || null, stopajRate: stopajRate || null, notes: notes || null,
          description: `Satış Faturası - ${selectedAccount.companyName}`, createdBy: ownAuthSession.getCurrentEmail() || 'System',
          eInvoiceProfileType, eInvoiceTypeCode, eInvoiceScenario, eInvoiceStatus: 'DRAFT',
          // ✅ Teklif ilişkisi (tekliften fatura oluşturuluyorsa)
          sourceOfferId: sourceOfferId || null,
          sourceOfferNumber: sourceOfferNumber || null,
          // ✅ Sipariş ilişkisi (siparişten fatura oluşturuluyorsa)
          sourceOrderId: sourceOrderId || null,
          sourceOrderNumber: sourceOrderNumber || null,
          // ✅ İrsaliye ilişkisi (irsaliyeden fatura oluşturuluyorsa)
          sourceWaybillId: sourceWaybillId || null,
          sourceWaybillNumber: sourceWaybillNumber || null,
          // ✅ İade ilişkisi (gönderilmiş bir faturadan iade oluşturuluyorsa)
          returnedInvoiceId: returnedInvoiceId || null,
          returnedInvoiceNumber: returnedInvoiceNumber || null,
          returnedInvoiceDate: returnedInvoiceDate || null,
          items: selectedItems.map((item, idx) => ({
            itemId: item.itemId.startsWith('manual_') ? null : item.itemId.toString(),
            itemName: item.itemName,
            isManualItem: item.itemId.startsWith('manual_'),
            quantity: item.quantity, unit: item.unit, unitPrice: item.unitPrice, 
            enteredPrice: item.enteredPrice,
            priceType: item.priceType || 'VAT_EXCLUDED',
            vatRate: item.vatRate, sctRate: item.sctRate || 0,
            discountRate: item.discountRate || 0, discountAmount: item.discountAmount || 0, 
            warehouseId: item.warehouseId ? item.warehouseId.toString() : null,
            lineNumber: idx + 1,
            ...invoiceLotPayload(item),
          }))
        };
        const url = editingInvoiceId ? ACCOUNTING_API.INVOICE_UPDATE(editingInvoiceId) : ACCOUNTING_API.INVOICE_CREATE_COMPLETE;
        console.log('� [FATURA] Source bilgileri (state):', {
          sourceOrderId,
          sourceOrderNumber,
          sourceOfferId,
          sourceOfferNumber,
          sourceWaybillId,
          sourceWaybillNumber,
        });
        console.log('🔍 [FATURA] DTO source alanları:', {
          'dto.sourceOrderId': dto.sourceOrderId,
          'dto.sourceOrderNumber': dto.sourceOrderNumber,
          'dto.sourceOfferId': dto.sourceOfferId,
          'dto.sourceOfferNumber': dto.sourceOfferNumber,
          'dto.sourceWaybillId': dto.sourceWaybillId,
          'dto.sourceWaybillNumber': dto.sourceWaybillNumber,
        });
        console.log('�📤 [FATURA] Gönderilen DTO:', dto);
        console.log('🔍 [FATURA] Items detayı:', JSON.stringify(dto.items, null, 2));
        const res = await accountingApi.post(url, dto);
        const result = res.data;
        console.log('📥 [FATURA] Backend yanıtı:', result);
        if (!result.success) throw new Error(result.message || `${docNoun.tr} kaydedilemedi`);
        setLotWarnings(result.lotWarnings ?? result.LotWarnings ?? []);
        setLotErrorLine(null);
        // ✅ İade köprüsü: butonla seçilen orijinal fatura referansını yeni faturanın id'sine bağla.
        // Prod accounting bu alanları henüz kalıcılaştırmadığından, gönderim modalının orijinali
        // otomatik seçebilmesi için tarayıcıda tutuyoruz (servis/derleme gerekmez).
        const newInvoiceId = result.data?.id || result.data?.invoiceId || result.invoiceId || result.id || editingInvoiceId || null;
        if (newInvoiceId && returnedInvoiceId) {
          try {
            localStorage.setItem(
              `gts_return_ref_${newInvoiceId}`,
              JSON.stringify({ id: returnedInvoiceId, number: returnedInvoiceNumber, date: returnedInvoiceDate })
            );
          } catch { /* yok say */ }
        }
        clearDraftFromLocalStorage(); setStockProcessSuccess(true);
        setTimeout(() => { setIsStockProcessModalOpen(false); setIsSuccessModalOpen(true); }, 2000);
      }
    } catch (err: any) {
      // Backend 400/500 gövdesinde asıl nedeni döner (message/error) — jenerik
      // "Request failed with status code 500" yerine onu göster.
      const body = err?.response?.data;
      const serverMsg = [body?.message, body?.error].filter(Boolean).join(' — ');
      setLotErrorLine(typeof (body?.lineNumber ?? body?.LineNumber) === 'number' ? (body?.lineNumber ?? body?.LineNumber) : null);
      setError(serverMsg || err.message);
      setIsStockProcessModalOpen(false);
    } finally { setLoading(false); }
  };

  // Taslak anahtarı mod bazlı: fatura eski anahtarı korur (geriye uyum), teklif/irsaliye/sipariş ayrışır
  const draftStorageKey = companyId ? `salesInvoiceDraft_${companyId}${mode === 'invoice' ? '' : `_${mode}`}` : '';

  const saveDraftToLocalStorage = (silent: boolean = false) => {
    if (!companyId) return;
    localStorage.setItem(draftStorageKey, JSON.stringify({ selectedAccount, invoiceDate, dueDate, selectedItems, notes, currency, exchangeRate, tevkifatRate, tevkifatType, showTevkifat, stopajRate, showStopaj, originalInvoiceTotal, savedAt: new Date().toISOString() }));
    if (silent) return;
    setShowDraftSavedAlert(true); setTimeout(() => setShowDraftSavedAlert(false), 3000);
  };

  const loadDraftFromLocalStorage = () => {
    if (!draftData) return;
    setSelectedAccount(draftData.selectedAccount); setInvoiceDate(draftData.invoiceDate); setDueDate(draftData.dueDate || '');
    setSelectedItems(draftData.selectedItems || []); setNotes(draftData.notes || ''); setCurrency(draftData.currency || 'TRY');
    setExchangeRate(draftData.exchangeRate || '1'); setTevkifatRate(draftData.tevkifatRate || "0"); setTevkifatType(draftData.tevkifatType || "");
    setShowTevkifat(draftData.showTevkifat || false); setStopajRate(draftData.stopajRate || 0); setShowStopaj(draftData.showStopaj || false);
    setOriginalInvoiceTotal(draftData.originalInvoiceTotal || ''); setIsDraftModalOpen(false); setDraftData(null);
  };

  const clearDraftFromLocalStorage = () => {
    if (companyId) localStorage.removeItem(draftStorageKey);
    // Başarılı kayıt sonrası ayrılma onayı sorulmasın
    setFormDirty(false);
  };

  useEffect(() => {
    if (!companyId || editingInvoiceId) return;
    const stored = localStorage.getItem(draftStorageKey);
    if (stored) { try { setDraftData(JSON.parse(stored)); setIsDraftModalOpen(true); } catch (e) { localStorage.removeItem(draftStorageKey); } }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, editingInvoiceId]);

  // ✅ Otomatik taslak: form doluyken her değişiklikte sessizce kaydet.
  // Sekme değişimi/sayfa kapanması ile kalemlerin kaybolmasını önler; form yeniden
  // açıldığında "kaydedilmiş taslak var" penceresi ile geri yüklenir.
  // Not: `loading` bilerek bağımlılıklarda değil — kayıt sonrası loading değişimi
  // temizlenen taslağı yeniden yazmasın diye yalnızca form verisi değişince çalışır.
  useEffect(() => {
    if (!companyId || editingInvoiceId || loading || isDraftModalOpen) return;
    const hasContent = !!selectedAccount || selectedItems.length > 0 || !!notes.trim();
    setFormDirty(hasContent);
    if (!hasContent) return;
    const t = setTimeout(() => saveDraftToLocalStorage(true), 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, editingInvoiceId, selectedAccount, selectedItems, notes, invoiceDate, dueDate, currency, exchangeRate, tevkifatRate, tevkifatType, stopajRate]);

  // Düzenleme modunda da form doluysa ayrılırken onay sorulsun; sayfadan çıkınca bayrağı temizle
  useEffect(() => {
    if (!editingInvoiceId) return;
    setFormDirty(selectedItems.length > 0 || !!selectedAccount);
  }, [editingInvoiceId, selectedItems, selectedAccount]);

  useEffect(() => () => setFormDirty(false), []);

  // ✅ PR3 (Invoice Seller Snapshot Refactor): Mount'ta satıcı snapshot çek.
  // Manuel fatura akışında ekran/şube yok → Companies kaynağı. Backend resolver authoritatif.
  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    getSellerByCompany()
      .then((snapshot) => {
        if (cancelled) return;
        setSeller(snapshot);
        console.log(
          '[SalesInvoice] 🏪 Seller snapshot:',
          `${snapshot.sellerCompanyName} (${snapshot.source}, franchise=${snapshot.isFranchiseInvoice})`,
        );
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('[SalesInvoice] Seller fetch hatası:', err);
        setSeller(null);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId]);
  
  useEffect(() => {
    if (editingInvoiceId || loading || selectedAccount || isDraftModalOpen || !companyId) return;
    const t = setTimeout(() => handleOpenAccountModal(), 500); return () => clearTimeout(t);
  }, [editingInvoiceId, loading, selectedAccount, isDraftModalOpen, companyId]);

  const loadCurrencies = async (cid: string) => {
    try {
      const { loadCompanyActiveCurrencies } = await import('./utils/loadCompanyActiveCurrencies');
      const curs = await loadCompanyActiveCurrencies(cid);
      setAvailableCurrencies(curs);
    } catch (e) {
      console.error(e);
      setAvailableCurrencies([{ code: 'TRY', active: true }]);
    }
  };

  const handleDisplayQuantityChange = (id: string, val: string) => {
    const s = val.replace(/\./g, ',');
    if ((s.match(/,/g) || []).length > 1 || (s !== '' && !/^[0-9]*[,]?[0-9]*$/.test(s))) return;
    setSelectedItems(selectedItems.map(i => i.id === id ? { ...i, displayQuantity: s } : i));
  };
  
  const handleQuantityCommitOnBlur = (id: string) => {
    setSelectedItems(selectedItems.map(i => {
      if (i.id !== id) return i;
      const raw = (i.displayQuantity ?? '').trim();
      const q = parseQuantityString(raw);
      // Alan boş bırakıldıysa metin boş kalır (otomatik "0" yazılmaz), hesaplamada 0 sayılır.
      // Miktar satır tutarını değiştirdiği için indirim metinleri modelden yeniden üretilir.
      return recalculateLineItem(clearDiscountDisplays({
        ...i,
        quantity: q,
        displayQuantity: raw === '' ? '' : formatQuantityForDisplay(q),
      }));
    }));
  };

  const handleBulkWarehouseSelect = () => {
    if (!bulkWarehouseId) return;
    setSelectedItems(selectedItems.map(i => ({ ...i, warehouseId: bulkWarehouseId })));
    setIsBulkWarehouseModalOpen(false); setBulkWarehouseId('');
  };

  const handleToggleAdditionalMenu = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleCloseMenu = () => setAnchorEl(null);
  const handleAddTevkifat = () => { setShowTevkifat(true); handleCloseMenu(); };
  const handleAddStopaj = () => { setShowStopaj(true); handleCloseMenu(); };

  // ✅ AI ajanı köprüsü: fatura + irsaliye + satış siparişi + satış teklifi.
  useInvoiceAgentBridge({
    formType: isWaybillMode
      ? 'waybill'
      : isOrderMode
        ? 'orderSales'
        : isOfferMode
          ? 'offerSales'
          : 'sales',
    enabled: mode === 'invoice' || mode === 'waybill' || mode === 'order' || mode === 'offer',
    selectAccount: (acc) => handleSelectAccount(acc as Account),
    setField: (field, value) => {
      switch (field) {
        case 'invoiceDate': setInvoiceDate(String(value)); return true;
        case 'dueDate': setDueDate(String(value)); return true;
        case 'invoiceNumber': setInvoiceNumber(String(value)); return true;
        case 'notes': setNotes(String(value)); return true;
        case 'currency': setCurrency(String(value).toUpperCase()); return true;
        case 'exchangeRate': setExchangeRate(String(value)); return true;
        case 'paymentMethod':
          if (isWaybillMode || isOrderMode || isOfferMode) return false;
          setPaymentMethod(String(value));
          return true;
        case 'showPrices':
          if (!isWaybillMode) return false;
          setShowPrices(value === true || value === 'true');
          return true;
        case 'deliveryDate':
          if (!isOrderMode) return false;
          setDeliveryDate(String(value));
          return true;
        case 'processStock':
          if (!isOrderMode) return false;
          setProcessStock(value === true || value === 'true');
          return true;
        case 'orderStatus': {
          if (!isOrderMode) return false;
          const st = String(value).toLowerCase();
          if (['draft', 'pending', 'confirmed', 'shipped', 'delivered', 'canceled'].includes(st)) {
            setOrderStatus(st as any);
            return true;
          }
          return false;
        }
        case 'validUntil':
          if (!isOfferMode) return false;
          setValidUntil(String(value));
          return true;
        case 'offerStatus': {
          if (!isOfferMode) return false;
          const st = String(value).toLowerCase();
          if (['draft', 'pending', 'approved', 'rejected'].includes(st)) {
            setOfferStatus(st as any);
            return true;
          }
          return false;
        }
        case 'tevkifatType': {
          if (isWaybillMode) return false;
          const code = String(value);
          setShowTevkifat(true); setTevkifatType(code);
          const t = tevkifatTypes.find(x => x.code === code);
          if (t) setTevkifatRate(t.rate);
          return true;
        }
        case 'stopajRate':
          if (isWaybillMode) return false;
          setShowStopaj(true); setStopajRate(Number(value) || 0);
          return true;
        default: return false;
      }
    },
    setItems: (updater) => setSelectedItems(prev => updater(prev)),
    getDefaultWarehouseId: () =>
      selectedWarehouseId || warehouses.find(w => w.isDefault)?.id || warehouses[0]?.id,
    getState: () => ({
      accountName: selectedAccount?.companyName ?? null,
      invoiceNumber, invoiceDate, dueDate, currency,
      items: selectedItems,
      subtotal, vatTotal, grandTotal,
      error,
    }),
    save: () => handleSaveInvoice(),
  });

  
  // ✅ Cari bilgilerini güncelle (sadece fatura snapshot'ı için)
  const handleAccountDataChange = (field: keyof Account, value: string) => {
    console.log('🔄 [CARİ DEĞİŞİKLİK]', field, ':', value);
    if (!selectedAccount) {
      console.warn('⚠️ [CARİ DEĞİŞİKLİK] selectedAccount null, güncelleme yapılamadı!');
      return;
    }
    
    const updatedAccount = {
      ...selectedAccount,
      [field]: value
    };
    
    console.log('✅ [CARİ DEĞİŞİKLİK] Yeni state:', updatedAccount);
    setSelectedAccount(updatedAccount);
  };

  // ✅ Modal açıldığında veya search/pagination değiştiğinde inventory yükle
  useEffect(() => {
    if (isItemModalOpen && companyId) {
      console.log('📦 [INVENTORY] Ürünler yükleniyor...', {
        companyId,
        warehouseId: selectedWarehouseId,
        search: itemSearchTerm,
        page: itemsPage,
        pageSize: itemsRowsPerPage
      });
      
      loadInventoryItemsPaged(
        companyId,
        itemsPage + 1,
        itemsRowsPerPage,
        itemSearchTerm,
        null, // categoryId
        null, // brandId
        selectedWarehouseId,
        null, // isActive
        null, // isEcommerce
        false, // sortNewestFirst
        false // hideZeroPrice
      );
    }
  }, [isItemModalOpen, companyId, selectedWarehouseId, itemSearchTerm, itemsPage, itemsRowsPerPage, loadInventoryItemsPaged]);
  
  return (
    <Box 
      sx={{ 
        width: '100%',
        p: 2,
      display: 'flex', 
        flexDirection: 'column'
      }}
    >
      <InvoiceHeader sx={{ flexShrink: 0, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={handleGoBack} sx={{ mr: 2 }}>{tt('Geri', 'Back')}</Button>
          <Typography variant="h5" component="h1" fontWeight="bold">{pageTitle}</Typography>
          <GuideButton guideKey="documents.salesInvoice" sx={{ ml: 1 }} />
        </Box>
        <Box>
          <Button variant="outlined" color="secondary" startIcon={<DraftsIcon />} disabled={loading || (!selectedAccount && selectedItems.length === 0)} sx={{ mr: 1 }} onClick={saveDraftToLocalStorage}>{tt('Taslak Kaydet', 'Save Draft')}</Button>
          <Tooltip title={isQuickSaleInvoice && editingInvoiceId ? `Bu bir Hızlı Satış faturasıdır (${quickSalePaymentType} ile ödendi). Kapalı faturalar düzenlenemez.` : ''}>
            <span>
              <Button variant="contained" color={isQuickSaleInvoice && editingInvoiceId ? 'inherit' : 'primary'} startIcon={<SaveIcon />} disabled={loading || (isQuickSaleInvoice && !!editingInvoiceId)} sx={{ mr: 1 }} onClick={() => handleSaveInvoice()}>
                {editingInvoiceId ? (isQuickSaleInvoice ? tt('Düzenlenemez', 'Not Editable') : tt('Güncelle', 'Update')) : tt('Kaydet', 'Save')}
              </Button>
            </span>
          </Tooltip>
          <Button variant="outlined" startIcon={<PrintIcon />} disabled={loading || selectedItems.length === 0} sx={{ mr: 1 }}>{tt('Yazdır', 'Print')}</Button>
          {/* PYS: belgeyi projeye bağlama (tüm modlarda, opsiyonel) — düğme + modal */}
          <ProjectPickerButton
            value={projectId}
            onChange={setProjectId}
            disabled={loading || isQuickSaleInvoice}
          />
        </Box>
      </InvoiceHeader>
      
      {error && <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>{error}</Alert>}
      {isQuickSaleInvoice && editingInvoiceId && <Alert severity="warning" sx={{ mb: 2, flexShrink: 0 }}><strong>⚠️ Kapalı Fatura:</strong> Bu bir Hızlı Satış faturasıdır ({quickSalePaymentType} ile ödendi). Ödemesi alınmış kapalı faturalar düzenlenemez.</Alert>}
      {showDraftSavedAlert && <Alert severity="success" sx={{ mb: 2, flexShrink: 0 }}>{tt('Taslak başarıyla kaydedildi! 💾', 'Draft saved successfully! 💾')}</Alert>}
      {orderStockHandedOver && !editingInvoiceId && <Alert severity="info" sx={{ mb: 2, flexShrink: 0 }}>{tt(ORDER_STOCK_HANDOVER_MESSAGE, 'Stock was processed at the order stage; when the invoice is saved, the order movement is transferred to the invoice.')}</Alert>}
      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', my: 2, flexShrink: 0 }}><CircularProgress /></Box>}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <AccountInfoForm 
            selectedAccount={selectedAccount} 
            onOpenAccountModal={handleOpenAccountModal} 
            disabled={loading || isQuickSaleInvoice}
            isEditMode={!!editingInvoiceId}
            onAccountDataChange={handleAccountDataChange}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <InvoiceInfoForm invoiceNumber={invoiceNumber} invoiceDate={invoiceDate} dueDate={dueDate} currency={currency} exchangeRate={exchangeRate} currencies={availableCurrencies} onInvoiceNumberChange={setInvoiceNumber} onInvoiceDateChange={setInvoiceDate} onDueDateChange={setDueDate} onCurrencyChange={(v) => { setCurrency(v); if (v === 'TRY') setExchangeRate('1'); }} onExchangeRateChange={setExchangeRate} disabled={loading || isQuickSaleInvoice} isEditMode={!!editingInvoiceId} mode={mode} />

          {/* ✅ TEKLİF MODU - GEÇERLİLİK TARİHİ VE DURUM */}
          {isOfferMode && (
            <Box sx={{ mt: 2 }}>
              <InvoiceSection>
                <SectionTitle>{tt('📋 Teklif Bilgileri', '📋 Offer Information')}</SectionTitle>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <AppDatePicker
                      fullWidth
                      label={tt('Geçerlilik Tarihi *', 'Validity Date *')}
                      value={validUntil}
                      onChange={setValidUntil}
                      disabled={loading}
                      size="small"
                      helperText={tt('Teklifin geçerli olduğu son tarih', 'Last date when this offer is valid')}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      select
                      label={tt('Teklif Durumu', 'Offer Status')}
                      value={offerStatus}
                      onChange={(e) => setOfferStatus(e.target.value as 'draft' | 'pending' | 'approved' | 'rejected')}
                      disabled={loading}
                      size="small"
                    >
                      <MenuItem value="draft">{tt('Taslak', 'Draft')}</MenuItem>
                      <MenuItem value="pending">{tt('Beklemede', 'Pending')}</MenuItem>
                      <MenuItem value="approved">{tt('Onaylandı', 'Approved')}</MenuItem>
                      <MenuItem value="rejected">{tt('Reddedildi', 'Rejected')}</MenuItem>
                    </TextField>
                  </Grid>
                </Grid>
              </InvoiceSection>
            </Box>
          )}
          
          {/* ✅ İRSALİYE MODU - FİYAT GÖSTERİMİ AYARI */}
          {isWaybillMode && (
            <Box sx={{ mt: 2 }}>
              <InvoiceSection>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {tt('📦 Fiyatları PDF/Yazdırmada Göster:', '📦 Show Prices in PDF/Print:')}
                  </Typography>
                  <Button
                    variant={showPrices ? "contained" : "outlined"}
                    color={showPrices ? "success" : "inherit"}
                    size="small"
                    onClick={() => setShowPrices(!showPrices)}
                    disabled={loading}
                    sx={{ minWidth: 150 }}
                  >
                    {showPrices ? tt('✓ Fiyatlar Görünür', '✓ Prices Visible') : tt('✗ Fiyatlar Gizli', '✗ Prices Hidden')}
                  </Button>
                </Box>
              </InvoiceSection>
            </Box>
          )}
          
          {/* ✅ SİPARİŞ MODU - TESLİM TARİHİ VE DURUM */}
          {isOrderMode && (
            <Box sx={{ mt: 2 }}>
              <InvoiceSection>
                <SectionTitle>{tt('📋 Sipariş Bilgileri', '📋 Order Information')}</SectionTitle>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <AppDatePicker
                      fullWidth
                      label={tt('Teslim Tarihi', 'Delivery Date')}
                      value={deliveryDate}
                      onChange={setDeliveryDate}
                      disabled={loading}
                      size="small"
                      helperText={tt('Opsiyonel - Tahmini teslim tarihi', 'Optional - estimated delivery date')}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      select
                      label={tt('Sipariş Durumu', 'Order Status')}
                      value={orderStatus}
                      onChange={(e) => setOrderStatus(e.target.value as any)}
                      disabled={loading}
                      size="small"
                    >
                      <MenuItem value="draft">{tt('Taslak', 'Draft')}</MenuItem>
                      <MenuItem value="pending">{tt('Beklemede', 'Pending')}</MenuItem>
                      <MenuItem value="confirmed">{tt('Onaylandı', 'Confirmed')}</MenuItem>
                      <MenuItem value="shipped">{tt('Sevk Edildi', 'Shipped')}</MenuItem>
                      <MenuItem value="delivered">{tt('Teslim Edildi', 'Delivered')}</MenuItem>
                      <MenuItem value="canceled">{tt('İptal', 'Canceled')}</MenuItem>
                    </TextField>
                  </Grid>
                </Grid>
              </InvoiceSection>
            </Box>
          )}
          
          {/* ✅ E-FATURA BİLGİLERİ - SADECE FATURA MODUNDA */}
          {!isOfferMode && !isWaybillMode && !isOrderMode && (
          <Box sx={{ mt: 2 }}>
            <EInvoiceDetailsForm profileType={eInvoiceProfileType} typeCode={eInvoiceTypeCode} scenario={eInvoiceScenario} onProfileTypeChange={setEInvoiceProfileType} onTypeCodeChange={handleEInvoiceTypeCodeChange} disabled={loading || isQuickSaleInvoice} mukellef={eInvoiceMukellef} mukellefLoading={eInvoiceMukellefLoading} mukellefTitle={eInvoiceMukellefTitle} />

            {/* ✅ İADE: tip kodu iade ise iade edilen orijinal fatura seçimi (zorunlu) */}
            {isReturnTypeSelected && (
              <Box sx={{ mt: 2 }}>
                <InvoiceSection>
                  <SectionTitle>↩️ {tt('İade Edilen Orijinal Fatura', 'Original Returned Invoice')}</SectionTitle>
                  {!selectedAccount?.id ? (
                    <Alert severity="info">{tt('Önce cari hesap seçin, ardından iade edilecek orijinal faturayı seçebilirsiniz.', 'Select an account first, then choose the original invoice to return.')}</Alert>
                  ) : isFromReturn && returnedInvoiceNumber ? (
                    <Alert severity="success">
                      {tt(`İade edilen orijinal fatura: ${returnedInvoiceNumber}`, `Original returned invoice: ${returnedInvoiceNumber}`)}
                    </Alert>
                  ) : (
                    <>
                      <FormControl fullWidth size="small">
                        <InputLabel>{tt('Orijinal Fatura', 'Original Invoice')}</InputLabel>
                        <Select
                          label={tt('Orijinal Fatura', 'Original Invoice')}
                          value={returnedInvoiceId || ''}
                          onChange={(e) => handleSelectReturnOriginal(String(e.target.value))}
                        >
                          <MenuItem value="">
                            <em>{loadingReturnCandidates ? tt('Yükleniyor...', 'Loading...') : (returnCandidates.length === 0 ? tt('Uygun fatura bulunamadı', 'No eligible invoices') : tt('Seçiniz', 'Select'))}</em>
                          </MenuItem>
                          {returnCandidates.map((c) => (
                            <MenuItem key={c.id} value={c.id}>
                              {c.invoiceNumber} · {c.invoiceDate ? new Date(c.invoiceDate).toLocaleDateString('tr-TR') : ''} · {Number(c.grandTotal || 0).toLocaleString('tr-TR')} {c.currency || 'TRY'}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Alert severity="warning" sx={{ mt: 1 }}>
                        {tt('İade faturası için orijinal fatura seçimi zorunludur. Kalemler kalan iade edilebilir miktarla otomatik doldurulur ve kalan miktarı aşan iadeler kabul edilmez.', 'Selecting the original invoice is required for return invoices. Items are auto-filled with remaining returnable quantity; returns exceeding the remaining quantity are rejected.')}
                      </Alert>
                    </>
                  )}
                </InvoiceSection>
              </Box>
            )}
        </Box>
          )}

          {/* ✅ İrsaliye ve Ödeme Bilgileri (E-Fatura için) - ŞİMDİLİK GİZLİ */}
          <Box sx={{ mt: 2, display: 'none' }}>
            <InvoiceSection>
              <SectionTitle>📦 İrsaliye ve Ödeme Bilgileri (E-Fatura İçin - Opsiyonel)</SectionTitle>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                      <TextField
                fullWidth
                    label="İrsaliye No"
                    value={despatchDocumentNumber}
                    onChange={(e) => setDespatchDocumentNumber(e.target.value)}
                    disabled={loading}
                        size="small"
                    placeholder="Ör: İRS-2025-0001"
                      />
                    </Grid>
          <Grid item xs={12} md={6}>
                  <AppDatePicker
                    fullWidth
                    label="İrsaliye Tarihi"
                    value={despatchDocumentDate}
                    onChange={setDespatchDocumentDate}
                    disabled={loading}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Ödeme Şekli"
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    disabled={loading}
                    size="small"
                    placeholder="Ör: Kredi Kartı, Nakit, Havale/EFT"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="IBAN / Hesap No"
                    value={iban}
                    onChange={(e) => setIban(e.target.value)}
                    disabled={loading}
                    size="small"
                    placeholder="TR00 0000 0000 0000 0000 0000 00"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Ödeme Notu"
                    value={paymentNote}
                    onChange={(e) => setPaymentNote(e.target.value)}
                    disabled={loading}
                    size="small"
                    placeholder="Ör: 30 gün vadeli"
                  />
                </Grid>
              </Grid>
            </InvoiceSection>
          </Box>
          </Grid>
        </Grid>
        
        <Grid container spacing={2}>
          <Grid item xs={12}>
          <InvoiceItemsTable selectedItems={selectedItems} currency={currency} exchangeRate={exchangeRate} loading={loading} unitOptions={unitOptions} onOpenItemModal={() => setIsItemModalOpen(true)} onAddManualItem={handleAddManualItem} onOpenBulkWarehouseModal={() => setIsBulkWarehouseModalOpen(true)} onItemNameChange={handleItemNameChange} onDisplayQuantityChange={handleDisplayQuantityChange} onQuantityCommitOnBlur={handleQuantityCommitOnBlur} onUnitChange={handleUnitChange} onPriceTypeChange={handlePriceTypeChange} onItemDisplayChange={handleItemDisplayChange} onItemNumberCommit={handleItemNumberCommit} discountMode={discountMode} onDiscountModeChange={handleDiscountModeChange} onDeleteItem={handleRemoveItem} mode={mode} onOpenCopyFromDocumentModal={() => setIsCopyFromDocumentModalOpen(true)} onBarcodeScanned={handleBarcodeScanned} attemptedSave={attemptedSave} lotMode={mode === 'invoice' ? (isReturnTypeSelected ? 'saleReturn' : 'sale') : undefined} onLotChange={handleLotChange} onSplitLot={handleSplitLot} errorLineNumber={lotErrorLine} />
              
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}>
                  <Box>
                <SectionTitle variant="h6" sx={{ fontSize: '0.9rem', mb: 1 }}>{tt('Notlar', 'Notes')}</SectionTitle>
                <TextField fullWidth multiline rows={2} placeholder={tt(`${docNoun.tr} ile ilgili notlar...`, `Notes about the ${docNoun.en}...`)} value={notes} onChange={(e) => setNotes(e.target.value)} disabled={loading} size="small" inputProps={{ style: { fontSize: '0.75rem' } }} />
                  </Box>
                  {showTevkifat && (
                    <Box sx={{ mt: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" sx={{ fontSize: '0.9rem', fontWeight: 'bold', mr: 1 }}>Tevkifat Bilgileri</Typography>
                    <IconButton size="small" color="error" onClick={handleRemoveTevkifat} sx={{ p: 0 }}><CloseIcon fontSize="small" /></IconButton>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>Tevkifat Tipi:</Typography>
                        <FormControl size="small" sx={{ width: '70%' }}>
                      <Select value={tevkifatType} onChange={handleTevkifatTypeChange} size="small" sx={{ fontSize: '0.75rem' }} displayEmpty>
                        <MenuItem value=""><em>Seçiniz</em></MenuItem>
                        {tevkifatTypes.map((type: TevkifatType) => <MenuItem key={type.code} value={type.code} sx={{ fontSize: '0.75rem' }}>{type.code} - {type.description.substring(0, 15)}...</MenuItem>)}
                          </Select>
                        </FormControl>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>Tevkifat Oranı:</Typography>
                        <FormControl size="small" sx={{ width: '70%' }}>
                      <Select value={tevkifatRate} onChange={handleTevkifatRateChange} size="small" sx={{ fontSize: '0.75rem' }}>
                            <MenuItem value="0">Yok</MenuItem>
                        {['1/10', '2/10', '3/10', '4/10', '5/10', '6/10', '7/10', '8/10', '9/10', '10/10'].map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                          </Select>
                        </FormControl>
                      </Box>
                    </Box>
                  )}
                  {showStopaj && (
                    <Box sx={{ mt: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" sx={{ fontSize: '0.9rem', fontWeight: 'bold', mr: 1 }}>Stopaj Bilgileri</Typography>
                    <IconButton size="small" color="error" onClick={handleRemoveStopaj} sx={{ p: 0 }}><CloseIcon fontSize="small" /></IconButton>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>Stopaj Oranı (%):</Typography>
                    <TextField type="number" size="small" value={stopajRate} onChange={handleStopajRateChange} inputProps={{ min: 0, max: 100, style: { fontSize: '0.75rem', padding: '2px 4px' }}} sx={{ width: '70%' }} />
                      </Box>
                    </Box>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <SectionTitle variant="h6" sx={{ fontSize: '0.9rem' }}>{tt('Toplamlar', 'Totals')}</SectionTitle>
                  <IconButton size="small" color="primary" onClick={handleToggleAdditionalMenu} sx={{ ml: 1 }}><AddIcon fontSize="small" /></IconButton>
                    </Box>
                    <Box>
                  <TotalLine><Typography variant="body2" sx={{ fontSize: '0.75rem' }}>{tt('Ara Toplam:', 'Subtotal:')}</Typography><Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.75rem' }}>{formatAmount(subtotal)} {getCurrencySymbol(currency)}</Typography></TotalLine>
                  {discountNetTotal > 0 && <TotalLine><Typography variant="body2" sx={{ fontSize: '0.75rem' }}>{tt('İndirim:', 'Discount:')}</Typography><Typography variant="body2" fontWeight="bold" color="error" sx={{ fontSize: '0.75rem' }}>-{formatAmount(discountNetTotal)} {getCurrencySymbol(currency)}</Typography></TotalLine>}
                  {discountNetTotal > 0 && <TotalLine><Typography variant="body2" sx={{ fontSize: '0.75rem' }}>{tt('İndirim Sonrası Ara Toplam:', 'Subtotal After Discount:')}</Typography><Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.75rem' }}>{formatAmount(netSubtotal)} {getCurrencySymbol(currency)}</Typography></TotalLine>}
                  {Object.entries(vatRateTotals).map(([rate, amount]) => <TotalLine key={`vat-${rate}`}><Typography variant="body2" sx={{ fontSize: '0.75rem' }}>KDV %{rate}:</Typography><Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.75rem' }}>{formatAmount(amount)} {getCurrencySymbol(currency)}</Typography></TotalLine>)}
                  {showTevkifat && tevkifatRate !== "0" && <TotalLine><Typography variant="body2" sx={{ fontSize: '0.75rem' }}>Tevkifat ({tevkifatRate}):</Typography><Typography variant="body2" fontWeight="bold" color="error" sx={{ fontSize: '0.75rem' }}>{formatAmount(tevkifatAmount)} {getCurrencySymbol(currency)}</Typography></TotalLine>}
                  {showStopaj && stopajRate > 0 && <TotalLine><Typography variant="body2" sx={{ fontSize: '0.75rem' }}>Stopaj (%{stopajRate}):</Typography><Typography variant="body2" fontWeight="bold" color="error" sx={{ fontSize: '0.75rem' }}>{formatAmount(stopajAmount)} {getCurrencySymbol(currency)}</Typography></TotalLine>}
                      <Divider sx={{ my: 0.5 }} />
                  <TotalLine><Typography variant="subtitle2" sx={{ fontSize: '0.85rem' }}>{tt('Genel Toplam:', 'Grand Total:')}</Typography><Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ fontSize: '0.9rem' }}>{formatAmount(grandTotal)} {getCurrencySymbol(currency)}</Typography></TotalLine>
                  {((showTevkifat && tevkifatRate !== "0") || (showStopaj && stopajRate > 0)) && (
                    <TotalLine>
                      <Typography variant="subtitle2" sx={{ fontSize: '0.85rem' }}>{tt('Ödenecek Tutar:', 'Payable Amount:')}</Typography>
                      <Typography variant="subtitle1" fontWeight="bold" color="success.main" sx={{ fontSize: '0.9rem' }}>{formatAmount(payableAmount)} {getCurrencySymbol(currency)}</Typography>
                    </TotalLine>
                  )}
                  {currency !== 'TRY' && (
                    <TotalLine>
                      <Typography variant="subtitle2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                        {tt('TL Karşılığı:', 'TRY Equivalent:')}
                      </Typography>
                      <Typography variant="subtitle2" color="primary" sx={{ fontSize: '0.8rem' }}>
                        {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' })
                          .format(grandTotal * (parseFloat(exchangeRate) || 1))}
                      </Typography>
                    </TotalLine>
                  )}
                    </Box>
                  </Box>
                </Grid>
              </Grid>
          </Grid>
        </Grid>

      <Box sx={{ height: '50px', flexShrink: 0 }} />

      {/* ✅ Modallar ve Menüler Container Dışında */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleCloseMenu} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <MenuItem onClick={handleAddTevkifat} disabled={showTevkifat}><AddCircleOutlineIcon fontSize="small" sx={{ mr: 1 }} />{tt('Tevkifat Ekle', 'Add Withholding')}</MenuItem>
        <MenuItem onClick={handleAddStopaj} disabled={showStopaj}><AddCircleOutlineIcon fontSize="small" sx={{ mr: 1 }} />{tt('Stopaj Ekle', 'Add Income Tax')}</MenuItem>
      </Menu>
      
      <AccountSelectionModal open={isAccountModalOpen} onClose={handleCloseAccountModal} accounts={filteredAccounts} searchTerm={accountSearchTerm} onSearchChange={handleAccountSearch} page={accountsPage} rowsPerPage={accountsRowsPerPage} onPageChange={handleAccountsPageChange} onRowsPerPageChange={handleAccountsRowsPerPageChange} onSelectAccount={handleSelectAccount} loading={loadingAccounts} />
      
      <Dialog open={isItemModalOpen} onClose={() => setIsItemModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle><Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>{tt('Stok Ürünü Seçimi', 'Stock Item Selection')}<IconButton onClick={() => setIsItemModalOpen(false)}><CloseIcon /></IconButton></Box></DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={8}><TextField fullWidth placeholder={tt('Ürün adı, barkod veya stok kodu...', 'Item name, barcode or stock code...')} value={itemSearchTerm} onChange={(e) => setItemSearchTerm(e.target.value)} InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>) }} /></Grid>
            <Grid item xs={12} sm={4}><FormControl fullWidth><InputLabel>{tt('Depo', 'Warehouse')}</InputLabel><Select value={selectedWarehouseId || "all"} onChange={(e) => setSelectedWarehouseId(e.target.value === "all" ? null : e.target.value)} label={tt('Depo', 'Warehouse')}><MenuItem value="all">{tt('Tüm Depolar', 'All Warehouses')}</MenuItem>{warehouses.map(w => <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>)}</Select></FormControl></Grid>
            </Grid>
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table stickyHeader size="small">
              <TableHead><TableRow><TableCell>{tt('Ürün Adı', 'Item Name')}</TableCell><TableCell>{tt('Stok Kodu', 'Stock Code')}</TableCell><TableCell align="right">{tt('Stok', 'Stock')}</TableCell><TableCell align="right">{tt('Fiyat', 'Price')}</TableCell><TableCell>{tt('İşlem', 'Action')}</TableCell></TableRow></TableHead>
              <TableBody>
                {inventoryLoading ? <TableRow><TableCell colSpan={5} align="center"><CircularProgress size={24} /> {tt('Ürünler yükleniyor...', 'Loading items...')}</TableCell></TableRow> : 
                 inventoryItems.length === 0 ? <TableRow><TableCell colSpan={5} align="center">{tt('Ürün bulunamadı.', 'No items found.')}</TableCell></TableRow> : 
                 inventoryItems.map(item => (
                      <TableRow key={item.id}>
                    <TableCell>{item.name}</TableCell><TableCell>{item.stockCode}</TableCell>
                    <TableCell align="right">{item.warehouseStocks?.reduce((sum, ws) => sum + ws.quantity, 0) || 0} {item.unit}</TableCell>
                        <TableCell align="right">{formatCurrency(item.salePrice)}</TableCell>
                    <TableCell><Button size="small" variant="contained" onClick={() => handleAddItem(item)}>{tt('Ekle', 'Add')}</Button></TableCell>
                      </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination component="div" count={totalCount} page={itemsPage} onPageChange={(e, p) => setItemsPage(p)} rowsPerPage={itemsRowsPerPage} onRowsPerPageChange={(e) => setItemsRowsPerPage(parseInt(e.target.value, 10))} rowsPerPageOptions={[10, 20, 50]} labelRowsPerPage={tt('Sayfa başına satır:', 'Rows per page:')} labelDisplayedRows={({ from, to, count }) => (isTr ? `${count} kayıttan ${from}-${to} arası` : `${from}-${to} of ${count}`)} />
        </DialogContent>
      </Dialog>
      
      <InvoiceSuccessModal open={isSuccessModalOpen} onClose={handleSuccessModalClose} invoiceNumber={invoiceNumber} isEditMode={!!editingInvoiceId} mode={mode} documentSide="sales" lotWarnings={lotWarnings} />
      <CopyItemsFromDocumentModal
        open={isCopyFromDocumentModalOpen}
        onClose={() => setIsCopyFromDocumentModalOpen(false)}
        mode={mode}
        initialSide="sales"
        currentDocumentId={editingInvoiceId || undefined}
        onItemsImport={handleImportItemsFromDocument}
      />
      
      <Dialog open={isDraftModalOpen} onClose={() => { setIsDraftModalOpen(false); setDraftData(null); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: 'warning.main', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HistoryIcon />
            {tt('Kaydedilmiş Taslak Bulundu', 'Saved Draft Found')}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ py: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 2 }}>
            <DraftsIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2 }} />
            <Typography variant="h6">{tt('Daha önce kaydedilmiş bir taslak var', 'There is a previously saved draft')}</Typography>
            <Typography variant="body2" color="text.secondary">
              {draftData?.savedAt && `${tt('Son kayıt', 'Last saved')}: ${new Date(draftData.savedAt).toLocaleString(isTr ? 'tr-TR' : 'en-US')}`}
            </Typography>
            {draftData?.selectedAccount && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1, width: '100%' }}>
                <Typography variant="caption">{tt('Cari', 'Account')}: <strong>{draftData.selectedAccount.companyName}</strong></Typography>
                <br />
                <Typography variant="caption">{tt('Ürün', 'Item')}: <strong>{draftData.selectedItems?.length || 0}</strong></Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => { clearDraftFromLocalStorage(); setIsDraftModalOpen(false); setDraftData(null); }} variant="outlined" color="error" fullWidth>
            {tt('Hayır, Yeni Oluştur', 'No, Create New')}
          </Button>
          <Button onClick={loadDraftFromLocalStorage} variant="contained" color="primary" fullWidth startIcon={<HistoryIcon />}>
            {tt('Evet, Taslağı Yükle', 'Yes, Load Draft')}
          </Button>
        </DialogActions>
      </Dialog>
      
      <PdfInvoiceUploadModal open={isPdfModalOpen} onClose={() => setIsPdfModalOpen(false)} onItemsLoaded={(pdfItems) => setSelectedItems([...selectedItems, ...pdfItems.map(i => recalculateLineItem(i))])} companyId={companyId || ''} />
      
      <Dialog open={isStockProcessModalOpen} onClose={() => {}} maxWidth="sm" fullWidth disableEscapeKeyDown>
        <DialogTitle sx={{ bgcolor: stockProcessSuccess ? 'success.main' : 'primary.main', color: 'white' }}><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>{stockProcessSuccess ? <CheckCircleOutlineIcon /> : <CircularProgress size={24} sx={{ color: 'white' }} />}<Typography variant="h6">{stockProcessSuccess ? tt('İşlemler Tamamlandı', 'Operations Completed') : tt('Stok ve Cari İşleniyor', 'Stock and Account Processing')}</Typography></Box></DialogTitle>
        <DialogContent sx={{ py: 3 }}><Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 2 }}>{stockProcessSuccess ? (<><CheckCircleOutlineIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} /><Typography variant="h6">{tt('Tüm işlemler başarıyla tamamlandı!', 'All operations completed successfully!')}</Typography>{lotWarnings.length > 0 && <Alert severity="warning" sx={{ mt: 2 }}>{lotWarnings.join(' ')}</Alert>}</>) : (<><CircularProgress size={64} sx={{ mb: 2 }} /><Typography variant="h6">{tt('İşlemler kaydediliyor...', 'Saving operations...')}</Typography></>)}</Box></DialogContent>
      </Dialog>
      
      <BulkWarehouseModal open={isBulkWarehouseModalOpen} onClose={() => setIsBulkWarehouseModalOpen(false)} warehouses={warehouses} selectedWarehouse={bulkWarehouseId} onWarehouseChange={setBulkWarehouseId} onApply={handleBulkWarehouseSelect} itemCount={selectedItems.length} />
      
      {/* ✅ SİPARİŞ STOK ONAY DİALOGU */}
      <Dialog open={showStockConfirmDialog} onClose={() => setShowStockConfirmDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: 'warning.main', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon />
            {tt('Stok İşleme Onayı', 'Stock Processing Confirmation')}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ py: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, textAlign: 'center' }}>
              {tt('Sipariş kaydedilirken stoktan düşülsün mü?', 'Should stock be deducted while saving the order?')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              {tt('Bu işlem geri alınamaz. Sipariş kaydedildiğinde seçilen ürünler stoktan düşülecektir.', 'This action cannot be undone. Selected items will be deducted from stock when the order is saved.')}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button 
            onClick={() => { setShowStockConfirmDialog(false); handleSaveInvoice(false); }} 
            variant="outlined" color="primary" fullWidth
          >
            {tt('Hayır, Stoktan Düşmesin', 'No, Do Not Deduct from Stock')}
          </Button>
          <Button 
            onClick={() => { setShowStockConfirmDialog(false); handleSaveInvoice(true); }} 
            variant="contained" color="warning" fullWidth
          >
            {tt('Evet, Stoktan Düşülsün', 'Yes, Deduct from Stock')}
          </Button>
        </DialogActions>
      </Dialog>
          </Box>
  );
};

export default SalesInvoice; 
